import { ChangeDetectorRef, Component, computed, effect, Injector, signal, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NFC } from '@awesome-cordova-plugins/nfc/ngx';
import { QRScanner, QRScannerStatus } from '@ionic-native/qr-scanner/ngx';
import { Vibration } from '@awesome-cordova-plugins/vibration/ngx';
import { ModalController, NavController, Platform, PopoverController } from '@ionic/angular';
import { EMPTY, Observable, of, Subject, Subscriber, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { OrderPipe } from 'ngx-order-pipe';
import { DeviceService } from 'src/app/services/device/device.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { CHANNELS, EventService } from 'src/app/services/events/event.service';
import { LogsService } from 'src/app/services/logs/logs.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { NfcService } from 'src/app/services/nfc/nfc.service';
import { PageStateService } from 'src/app/services/page-state/page-state.service';
import { PagingService } from 'src/app/services/paging/paging.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { IPrintJob } from 'src/app/services/print-job/models/print-job.model';
import { PrintJobService, PRINT_JOB_STATUS } from 'src/app/services/print-job/print-job.service';
import { IPrinter } from 'src/app/services/printer/models/printer.model';
import { IQueue } from 'src/app/services/printer/models/queue.model';
import { PrinterService } from 'src/app/services/printer/printer.service';
import { PrinterQueueCleanupService } from 'src/app/services/printer-queue-cleanup/printer-queue-cleanup.service';
import { ReleasePrintService } from 'src/app/services/release-print/release-print.service';
import { ReleaseResourceService } from 'src/app/services/release-resource/release-resource.service';
import { SecurePrintService } from 'src/app/services/secure-print/secure-print.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { isArray, isEmptyString } from 'src/app/services/utils/utils';
import * as _ from 'lodash';
import { PopoverComponent } from '@app/components/popovers/popover/popover.component';
import { ModalComponent } from '@app/components/modals/modal/modal.component';
import { UserService } from 'src/app/services/user/user.service';
import { Logger, LoggingService } from 'ionic-logging-service';
import { OrderByPipe } from '@app/pipes/order-by/order-by.pipe';
import { PrintJobByPrinterPipe } from '@app/pipes/print-job-by-printer/print-job-by-printer.pipe';
import { LocaleService } from 'src/app/services/language/locale.service';
import { ISignInMethods } from '@app/services/user/models/user.model';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { EmailTooltipComponent } from '@app/components/popovers/email-tooltip/email-tooltip.component';

export const SECURE_PRINT_LEVELS = {
  QR: 'QR_SCAN',
  NFC: 'NFC_SCAN',
  ON_DEVICE_RELEASE: 'ON_DEVICE_RELEASE'
};

@Component({
    selector: 'app-print-jobs',
    templateUrl: './print-jobs.page.html',
    styleUrls: ['./print-jobs.page.scss'],
    standalone: false
})
export class PrintJobsPage implements OnInit, OnDestroy {

  public  printerList = signal<Array<IPrinter>>([]);
  public  printJobList = signal<Array<IPrintJob>>([]);
  public  selectedPrintJobs = signal<Array<IPrintJob>>([]);

  public scanning = signal(false);
  public dataIsLoaded = signal(false);
  public invalidQRCode = signal(false);
  public showTransparentBody = signal(false);
  
  public isCordova: boolean = this.platformService.isCordova();
  public isIPhone: boolean = this.platformService.isIPhone();
  public isIOS: boolean = this.platformService.isIOS();
  public isAndroid: boolean = this.platformService.isAndroid();

  public appIsActive = signal(true);

  // Timestamp of the last resume-triggered reload; de-dupes platform.resume + PLATFORM_RESUMED so we refetch only once.
  private lastResumeReloadAt = 0;
  // Window within which a second resume path is treated as the same foreground.
  private readonly RESUME_RELOAD_DEDUP_MS = 1500;

  // Helper computed signal for common guard pattern
  private shouldProcessEvents = computed(() => !this.scanning() && this.appIsActive());
  public batchExecutedSubscription: Subscription = null;
  public  cloudStorage: boolean = false;
  public componentSubscription = new Subject<void>();
  public  currentBaseUrl: string = null;
  public  currentLanguage: string = null;
  public debouncerRunning = signal(false);
  public debounceTimer;
  public  deletedPrintJobPrinterList: Array<IPrinter> = [];
  // Tombstone guard filtering deleted/released jobs from stale GET responses (see applyServerJobList() / ionViewWillEnter()).
  private pendingDeletedJobUrls = new Set<string>();
  // Monotonic fetch counter — drops out-of-order GET responses in applyServerJobList().
  private fetchSeq = 0;
  private appliedFetchSeq = 0;
  public  errorMsg: string;
  public  favoriteQueueUrls: Array<string> = [];
  public  gettingPrinter: boolean = false;
  public isOnline = this.networkService.hasInternetAccess;
  public  isSelected: boolean = false;
  private  tooManyPrintJobsInReleaseErrorParameters: string[] = [];
  public nav: any;
  public platformResumeEventSubscription: Subscription = null;
  public platformResumeBroadcastEventSubscription: Subscription = null;
  public platformPauseEventSubscription: Subscription = null;
  public reloadPageEventSubscription: Subscription = null;
  public tenantUpdatedEventSubscription: Subscription = null;
  public updateSecurePrintSubscription: Subscription = null;
  public jobAddedSubscription: Subscription = null;
  public jobRemovedSubscription: Subscription = null;
  public jobUpdatedSubscription: Subscription = null;
  public releaseUpdatedSubscription: Subscription = null;
  public createdReleaseSubscription: Subscription = null;
  public lastUsedPrinterUrl: string = null;
  public languageChangeSubscription: Subscription = null;
  public  lastUsedAnywhereQueue = signal<IQueue | null>(null);
  private logger: Logger;
  public  networks: Array<string> = [];
  public nfcEnabled: boolean = false;
  private nfcReaderFlags = null;
  private nfcReaderMode = null;
  public nfcListenerSubscription: Subscription = null;
  public nfcReadyPopover: any;
  public nfcSubscription = new Subject<void>();
  public getTenantAndUserSubscription = new Subject<void>();
  public noProxiesForPrinter = signal(false);
  public patchErrorCount: number = 0;
  public  pinnedPrinterUrls: any = null;
  public  printAnywhere = signal<boolean | null>(null);
  public  printerAccessError: boolean = false;
  public printerInErrorState = signal(false);
  public  printJobsPageType: string;
  public  printerOffline = signal(false);
  public  printerQueue: IPrinter = null;
  public printerRemovedSubscription: Subscription = null;
  public printerStatusSubscription: Subscription = null;
  public printerUpdatedSubscription: Subscription = null;
  public printQueueRemovedSubscription: Subscription = null;
  public printQueueUpdatedSubscription: Subscription = null;
  public printQueueGroupsUpdatedSubscription: Subscription = null;
  public  printerUrlString: string;
  public  printJobPrinterLinks: Array<any> = [];
  public printJobsToAdd: Array<string> = [];
  public refresher: any = null;
  public releaseNotPossible = signal(false);
  public  releaseFailed: boolean = false;
  public  releaseResource: any = null;
  public scannedNfcId: string = null;
  public scannedPrinter: IPrinter = null;
  public scannedQRUrl: string = null;
  public scannedSignId: string = null;
  public scanningMethod: string = null;
  public  securePrinting: boolean = false;
  public securePrintLevel = signal<string | null>(null);
  public securePrintMethods: Array<string> = [];
  private selectedPrinterUrl = signal<string | null>(null);
  public selectedPrinter: IPrinter | null = null;
  public selectedQueue: IQueue = null;
  public selectAllClicked: boolean = false;
  public  storedNetworkFilters: Array<any> = [];
  public  storedNetworkFiltersUrl: Array<string> = [];
  public  successMsg: string;
  public tenant = this.tenantService.tenantSignal;
  public  tenantNetworks: any;
  public  unavailablePrintJobs = signal(false);
  public unSubscribe = new Subject<void>();
  public unSubscribeQRScanner = new Subject<void>();
  public unSubscribeMatchingQueues = new Subject<void>();
  public unSubscribeChangePrinterQueuePinStatus = new Subject<void>();
  // Cancels any in-flight release-resource create/update when superseded by a new debounce or a delete click, so a stale response can't resurrect releaseResource.
  private cancelPendingReleaseUpdate = new Subject<void>();
  public user = this.userService.userSignal;
  public userEmail: string = '';

  public signInMethods: ISignInMethods = {
    LOCAL_AD: 'AD',
    AZURE: 'Microsoft',
    EMAIL: 'Email',
    GOOGLE: 'Google',
    OKTA: 'Okta',
    ONELOGIN: 'OneLogin',
    VERTICAL: 'Vertical',
    client_credentials: 'API',
    KIOSK: 'Kiosk',
    OIDC: 'OIDC'
  };

  constructor(
    private broadcaster: Broadcaster,
    private deviceService: DeviceService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventService: EventService,
    private localeService: LocaleService,
    private logsService: LogsService,
    private loggingService: LoggingService,
    private modalCtrl: ModalController,
    public  navCtrl: NavController,
    private networkService: NetworkService,
    private nfc: NFC,
    private nfcService: NfcService,
    private orderPipe: OrderPipe,
    private orderByPipe: OrderByPipe,
    private pageTitleService: PageTitleService,
    private pageStateService: PageStateService,
    private pagingService: PagingService,
    public platform: Platform,
    private platformService: PlatformService,
    public  popoverCtrl: PopoverController,
    private printerService: PrinterService,
    private printerQueueCleanupService: PrinterQueueCleanupService,
    private printJobByPrinterPipe: PrintJobByPrinterPipe,
    private printJobService: PrintJobService,
    private qrScanner: QRScanner,
    private releasePrintService: ReleasePrintService,
    private releaseResourceService: ReleaseResourceService,
    private route: ActivatedRoute,
    private securePrintService: SecurePrintService,
    private tenantService: TenantService,
    public  userService: UserService,
    private vibration: Vibration,
    private changeDetectorRef: ChangeDetectorRef,
    private injector: Injector
  ) {
    this.printJobsPageType = this.route.snapshot.data['pageType'] || 'PrintJobsPage';
    
    // Keeps selectedPrinter synced with printerList so Pusher status updates are reflected in the UI in real time.
    effect(() => {
      const url = this.selectedPrinterUrl();
      const printerList = this.printerList();
      
      if (!url) {
        // No printer selected
        return;
      }
      
      // Only update selectedPrinter if it's in printerList — selectedPrinter can be any printer on the network, not just ones with jobs.
      const updatedPrinter = printerList.find(p => p.links.self === url);
      if (updatedPrinter && this.selectedPrinter) {
        // Update selectedPrinter with fresh data from printerList
        this.selectedPrinter = updatedPrinter;
      }
    });
    
    this.platform.ready().then(() => {
      this.logger = this.loggingService.getLogger("[" + this.printJobsPageType + "]");
      const methodName = "ctor";
      this.logger.entry(methodName);

      if (this.isAndroid && this.printJobsPageType === 'PrintJobsPage') {
        // Deliberately empty: lowest priority + no processNextHandler() stops the back-button chain, suppressing default app-exit here.
        this.platform.backButton.subscribeWithPriority(-1, () => {});
      }
    });
  }

  public userSignInMethod = computed(() => {
    const user = this.user();
    this.userEmail = this.user() && this.user().embedded.user.email ? this.user().embedded.user.email : '';
    if (
      user &&
      user.embedded?.authentication_details &&
      user.embedded.authentication_details.source
    ) {
      return this.signInMethods[user.embedded.authentication_details.source] || '';
    }
    return '';
  });

  async showEmailTooltip(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: EmailTooltipComponent,
      event: event,
      componentProps: {
        email: this.userEmail
      },
      cssClass: this.isCordova ? 'email-tooltip' : 'email-tooltip browser',
      showBackdrop: false,
      backdropDismiss: true,
      side: 'top',
      alignment: 'center',
      arrow: false,
      size: 'cover' // Let content determine size
    });

    await popover.present();

    setTimeout(() => {
      popover.dismiss();
    }, 2500);
  }

  private setPreventNFCListener() {
    this.logger.info('settingNFCListener()');
    this.nfcReaderFlags = this.nfc.FLAG_READER_NFC_A | this.nfc.FLAG_READER_NFC_V;

    this.nfc.readerMode(this.nfcReaderFlags).subscribe(
        (tag) => {
          console.log(tag);
        },
        (err) => {
          console.log('Error reading tag', err);
        }
    );
  }

  public filteredPrintJobs(printJobList: Array<IPrintJob>, printerID: string) {
    let sortedPrintJobByPrinter: Array<IPrintJob>;
    sortedPrintJobByPrinter = this.printJobByPrinterPipe.transform(printJobList, printerID);
    return sortedPrintJobByPrinter;
  }

  private setPageTitle(): void {
    const pageType = this.printJobsPageType;
    const pageTitle = pageType === 'PrintJobsPage' ? 'Print' : 'Recents';
    this.pageTitleService.setTitle(pageTitle);
  }

  ngOnInit(): void {
    this.setPageTitle();
    this.platform.ready().then(async () => {
      const methodName = 'ngOnInit() ';
      this.logger.info(methodName);

      const pinnedPrinterUrls = this.user()?.userAppSettings.pinnedPrinters || [];
      this.favoriteQueueUrls = this.user()?.userAppSettings.pinnedPrinterQueues || [];

      this.logger.info(methodName + 'favoriteQueuesCount: ' + this.favoriteQueueUrls.length);

      // Remove legacy pinned printers (can be removed when no users have them)
      if (pinnedPrinterUrls.length !== 0) {
        try {
          await this.printerQueueCleanupService.removePinnedPrinters(pinnedPrinterUrls);
        } catch (error) {
          this.logger.error('Error removing pinned printers', error);
        }
      }

      // Remove unavailable queues from favorites
      if (this.favoriteQueueUrls.length > 0) {
        try {
          this.favoriteQueueUrls = await this.printerQueueCleanupService.removeUnavailablePrinterQueues(
            this.favoriteQueueUrls,
            this.user()
          );
        } catch (error) {
          this.logger.error('Error removing unavailable printer queues', error);
        }
      }

      if (this.isCordova) {
        this.deviceService.registerDeviceToPrintix(this.tenant(), this.user());
      }

      // Listen for network status changes
      this.setupNetworkListener();
    });
  }

  private setupNetworkListener(): void {
    let wasOffline = false;

    effect(() => {
      const online = this.isOnline();
      this.logger.info(`Network status changed: ${online ? 'online' : 'offline'}`);

      if (!online) {
        wasOffline = true;
        return;
      }

      // Keep wasOffline=true until we actually refresh, so a deferred refresh still fires once appIsActive/scanning allow it (e.g. after foregrounding).
      if (wasOffline && this.appIsActive() && !this.scanning()) {
        this.logger.info('Network restored - refreshing print job list');
        this.loadPrintPage();
        wasOffline = false;
      }
    }, { injector: this.injector });
  }



  // ############### REFRESH ###############################################################################

  public loadPrintPage(refresher?): void {
    this.logger.info('loadPrintPage()');

    // Store the refresher event (only exists for user-initiated pull-to-refresh)
    const isUserInitiated = !!refresher;
    this.refresher = refresher || null;

    if (!this.isOnline() && this.refresher) { // prevent refresher from getting stuck, when internet connection is out
      this.refresher.target.complete();
      return; // Don't proceed if offline
    }

    // Always show loading spinner to prevent user interaction during data load
    const showSpinner = true;

    const executeRefresh = () => {
      this.dataIsLoaded.set(false); // to prevent empty favoritePrinters popover

      // Get Tenant and User every time the page is refreshed
      this.tenantService.refreshStoredTenant()
      .pipe(
        tap(() => this.logger.info('Tenant refreshed')),
        switchMap(() => this.userService.refreshCurrentUser()),
        tap(() => {
          this.logger.info('User refreshed');
          
          // Update local properties from refreshed user data
          this.lastUsedPrinterUrl = this.user()?.userAppSettings.lastUsedPrinter || null;
          this.favoriteQueueUrls = this.user()?.userAppSettings.pinnedPrinterQueues || [];
          this.printerUrlString = this.pagingService.getUrlString(0, 500);
          
          if (this.user()?.embedded.user.language) {
            this.currentLanguage = this.localeService.formatLocaleString(this.user().embedded.user.language);
          }
          
          this.securePrintMethods = this.user()?.userAppSettings.securePrintMethods || [];
        }),
        catchError((error) => {
          this.dialogService.hideLoadingSpinnerDialog('loadPrintPage() error');
          this.errorService.handleHttpClientResponseError(error, 'GET', 'loadPrintPage()')
            .subscribe(result => {
              if (result === 'unauthenticated') {
                this.logger.info('User unauthenticated');
              }
            });
          return EMPTY;
        })
      ).subscribe(() => {
        this.initiatePage();
      });
    };

    if (showSpinner) {
      this.dialogService.showLoadingSpinnerDialog('loadPrintPage()').subscribe(() => {
        executeRefresh();
      });
    } else {
      // Silent refresh for background updates (Pusher events)
      executeRefresh();
    }
  }

  // ############### /REFRESH ###############################################################################

  // ############### INIT ###############################################################################

  private initiatePage() {
    this.logger.info('initiatePage()');
    
    this.securePrintService.getUserSecurityLevel()
    .pipe(
      tap((securePrintLevel) => {
        this.securePrintLevel.set(securePrintLevel);
        this.pageStateService.setSecurePrintLevel(this.securePrintLevel());
        this.pageStateService.setPrintJobsPageType(this.printJobsPageType);
      }),
      switchMap(() => this.securePrintService.setSecurePrintMethods(this.user())),
      tap((securePrintMethods) => {
        this.securePrintMethods = securePrintMethods;
        this.handleNfcAvailability();
      }),
      switchMap(() => this.printerService.getSinglePrinter(this.lastUsedPrinterUrl, this.printerUrlString)),
      tap((lastUsedPrinter) => {
        if (lastUsedPrinter.length > 1) {
          this.logsService.logLastUsedPrinter(lastUsedPrinter[0], this.printJobsPageType);
          this.selectedPrinter = lastUsedPrinter[0];
          this.selectedPrinterUrl.set(lastUsedPrinter[0].links.self);
          this.checkForLastUsedQueue();
        } else {
          this.selectedPrinter = null;
          this.selectedPrinterUrl.set(null);
        }
        this.logger.info('refreshPrintJobsAndPrinters()');
      }),
      switchMap(() => this.refreshPrintJobsAndPrinters()),
      catchError((error) => {
        this.logger.error('initiatePage() - ERROR', error);
        this.errorService.handleHttpClientResponseError(error, 'GET', 'initiatePage()')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        return of([]); // Return empty array to continue the stream
      }),
      finalize(() => {
        this.dialogService.hideLoadingSpinnerDialog('initiatePage() complete');
      })
    ).subscribe((printerList: Array<IPrinter>) => {
      // Always set dataIsLoaded to true (even with no printers) so the empty-state renders on both print and recents pages.
      this.dataIsLoaded.set(true);
      
      if (printerList.length > 0) {
        if (!this.hasValidSelectedPrintJobs()) {
          this.selectedPrintJobs.set([]);
          this.resetPrintersAllowedToPrintProperty();
          if (this.scanning() && this.scanningMethod === 'QR_SCAN') {
            this.concludeScan();
          }
        }
        
        // Now safe to create release on page refresh
        this.logger.info('About to create release on page refresh');
        this.createRelease(null, false);
      }
      
      if (this.refresher) {
        this.refresher.target.complete();
      }
    });
  }

  /** Lightweight refresh (print jobs + printers only) for Pusher/user actions that don't need a full tenant/user refresh; returns a composable Observable. */
  private refreshPrintJobsAndPrinters(): Observable<Array<IPrinter>> {
    this.logger.info('refreshPrintJobsAndPrinters() - lightweight refresh');
    const seq = ++this.fetchSeq;
    
    return this.printJobService.getPrintJobList()
    .pipe(
      switchMap((printJobList: Array<IPrintJob>) => {
        const applied = this.applyServerJobList(printJobList, seq);
        if (!applied) {
          return of(this.printerList());
        }
        return this.printerService.getPrintersWithPrintJobs(this.printJobList(), this.printerUrlString)
          .pipe(
            map((printerList: Array<IPrinter>) => {
              if (this.isStaleFetch(seq)) {
                this.logger.info(`refreshPrintJobsAndPrinters() - dropped stale printer response (seq=${seq}, applied=${this.appliedFetchSeq})`);
                return this.printerList();
              }
              this.printerList.set(printerList);
              if (printerList.length > 0) {
                this.setPrinterLocation();
              }
              this.logger.info(`Printers loaded: ${printerList.length}`);
              return printerList;
            })
          );
      }),
      catchError((error) => {
        this.logger.error('refreshPrintJobsAndPrinters() - ERROR', error);
        this.errorService.handleHttpClientResponseError(error, 'GET', 'refreshPrintJobsAndPrinters()')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        return of([]);
      })
    );
  }

  private handleNfcAvailability(): void {
    if (!this.nfcEnabled) {
      this.logger.info('remove NFC from secure print methods, if it is not enabled on the device');
      const nfcIndex: number = this.securePrintMethods.indexOf('NFC');
      if (nfcIndex !== -1) {
        this.securePrintMethods.splice(nfcIndex, 1);
      }
    }
  }

  private hasValidSelectedPrintJobs = function(): boolean {
    this.logger.info('hasValidSelectedPrintJobs()');
    let hasValidPrintJobsSelected: boolean = false;
    if (this.selectedPrintJobs().length > 0) {
      for (let i = 0; i < this.printJobList().length; i++) {
        for (let j = 0; j < this.selectedPrintJobs().length; j++) {
          const printJob = this.printJobList()[i];
          if (printJob.links.self === this.selectedPrintJobs()[j].links.self) {
            hasValidPrintJobsSelected = true;
            printJob.options.isSelected = true;
            this.isAllSelected(this.findPrinterForPrintJob(printJob));
          }
        }
      }
    }
    return hasValidPrintJobsSelected;
  };

  // releasePrinterUrl names the printer the caller derived its queue from; without it the pair comes
  // from two sources and the server can reject it with 404 PRINT_QUEUE_NOT_FOUND.
  public createRelease(releaseQueue?: any, scanStartedForRelease?: boolean, releasePrinterUrl?: string): void {
    const methodName = 'createRelease()';
    this.logger.info(methodName);
    const releaseObject: object = {};
    const printerUrl: string = releasePrinterUrl ?? (this.selectedPrinter ? this.selectedPrinter.links.self : null);
    // Anywhere queue only on the Anywhere path: a direct job's queue is fixed server-side.
    const anywhereQueue = this.printAnywhere() ? this.lastUsedAnywhereQueue() : null;
    const candidateQueueUrl: string = releaseQueue ?? (anywhereQueue ? anywhereQueue.links.self : null);
    const queueUrl: string = this.queueUrlBelongsToPrinter(candidateQueueUrl, printerUrl) ? candidateQueueUrl : null;
    if (!queueUrl && candidateQueueUrl) {
      this.logger.info(methodName + ' dropping a queue that is not on the printer');
    }
    // Send the printer even without a queue: the server resolves the queue and the release stays
    // startable, where sending neither leaves printerQueue null.
    if (printerUrl) {
      releaseObject['printer'] = printerUrl;
      if (queueUrl) {
        releaseObject['queue'] = queueUrl;
      }
    }

    if (this.selectedPrintJobs().length !== 0) {
      const selectedJobs: Array<string> = [];
      this.selectedPrintJobs().forEach(job => {
        selectedJobs.push(job.links.self);
      });
      releaseObject['jobs'] = selectedJobs;
    }

    // Cancel any prior in-flight create/update; since cancelling fires its finalize synchronously (debouncerRunning -> false), re-assert debouncerRunning=true right after.
    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(true);

    this.releaseResourceService.createReleaseResource(releaseObject)
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => { this.debouncerRunning.set(false); }))
    .subscribe((response) => {
      this.logger.info(methodName + 'createReleaseResource() - ReleaseID: ' + response['resourceId']);
      this.dialogService.hideLoadingSpinnerDialog('createRelease()');
      this.releaseResource = response;
      this.unavailablePrintJobs.set(false);
      this.setPrinterAccessError(response.printerQueue);
      this.setReleaseJobsErrors(response.releaseJobs);

      if (scanStartedForRelease) {
        this.findScannedPrinter();
      }

      this.debouncerRunning.set(false);
    }, (error) => {
      // Without a release resource the next Print cannot work, so the failure has to reach the
      // user rather than stopping at a log line.
      this.dialogService.hideLoadingSpinnerDialog('ERROR - createRelease()');
      this.logger.info(methodName + 'createReleaseResource() error - ' + JSON.stringify({ status: error?.status }));
      this.debouncerRunning.set(false);
      this.errorService.handleHttpClientResponseError(error, 'POST', '[ReleaseResourceService] createReleaseResource()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  private setPrinterAccessError(printerQueue): void {
    const methodName = 'setPrinterAccessError()';
    this.logger.info('setPrinterAccesError()');
    if (printerQueue && printerQueue.errors.length !== 0) {
      this.printerAccessError = true;
      this.noProxiesForPrinter.set(false);
      this.releaseNotPossible.set(false);
      this.printerInErrorState.set(false);
      this.printerOffline.set(false);
      this.printerQueue = printerQueue.selectedPrinter;
      this.setPrinterQueueErrors(printerQueue);
    } else {
      this.noProxiesForPrinter.set(false);
      this.releaseNotPossible.set(false);
      this.printerAccessError = false;
      this.printerInErrorState.set(false);
      this.printerOffline.set(false);
    }
  }

  private setPrinterQueueErrors(printerQueue) {
    this.logger.info('setPrinterQueueErrors()');
    console.log('printerQueue errors:', printerQueue.errors);
    printerQueue.errors.forEach(error => {
      switch(error['type']) {
        case 'RELEASE_NOT_POSSIBLE':
          this.releaseNotPossible.set(true);
        break;
        case 'NO_PROXIES':
          this.noProxiesForPrinter.set(true);
        break;
        case 'PRINTER_IN_ERROR_STATE':
          if (this.selectedPrinter && this.selectedPrinter.embedded.status.pollStatus && this.selectedPrinter.embedded.status.pollStatus === 'DEVICE_NOT_FOUND') {
            this.printerOffline.set(true);
          }
          if (this.selectedPrinter && this.selectedPrinter.embedded.status.errors.length !== 0) {
            this.selectedPrinter.embedded.status.errors.forEach(printerError => {
              if (printerError === 'OFFLINE') {
                this.printerOffline.set(true);
              }
            });
          }
          this.printerInErrorState.set(true);
        break;
      }
    });
  }

  private setReleaseJobsErrors(releaseJobs) {
    this.logger.info('setReleaseJobsErrors()');
    if (this.selectedPrintJobs().length > 0) {
      this.selectedPrintJobs().forEach(selectedPJ => {
        releaseJobs.forEach(resourcePj => {
          if (selectedPJ.links.self === resourcePj.jobId) {
            if (resourcePj.errors.length === 0) {
              this.printJobList().forEach(pj => {
                if (resourcePj.jobId === pj.links.self) {
                  pj.options.resourceErrors = [];
                }
              });
            } else {
              this.unavailablePrintJobs.set(true);
              this.printJobList().forEach(pj => {
                if (resourcePj.jobId === pj.links.self) {
                  pj.options.resourceErrors = resourcePj.errors;
                }
              });
            }
          }
        });
      });
    }
  }

  private resetPrintersAllowedToPrintProperty(): void {
    this.logger.info('resetPrintersAllowToPrintProperty()');
    _.forEach(this.printerList(), (printer) => {
      printer.options.allowToPrint = true;
      printer.options.isAllPrintJobsSelected = false;
    });
  }

  private setPrinterLocation(): void {
    this.logger.info('setPrinterLocation()');
    for (let j = 0; j < this.printJobList().length; j++) {

      for (let i = 0; i < this.printerList().length; i++) {
        if (!(this.printerList()[i].location) || isEmptyString(this.printerList()[i].location)) {
          this.printerList()[i].location = null;
        }
      }
    }
  }

  // BE CAREFUL when you change last used queue stuff, since it can really mess up the app with errors if the is a mismatch between last used printer and queue.
  private checkForLastUsedQueue(): void {
    const methodName = 'checkForLastUsedQueue() ';
    this.logger.info(methodName);
    
    // Check if we have a selected printer
    if (!this.selectedPrinter) {
      this.logger.info(methodName + 'No selected printer');
      this.resetPrinterAndQueue();
      return;
    }
    
    const lastUsedAnywhereQueueUrl: string = this.user() && this.user().userAppSettings.lastUsedQueue ? this.user().userAppSettings.lastUsedQueue : null; // lastUsedAnywhereQueue is set in UserAppSettings
    if (lastUsedAnywhereQueueUrl !== null) {
      let printerHasLastUsedQueue = false;  // find lastUsedPrinter from favoritePrinterList
      this.selectedPrinter.queues.forEach(q => {
        if (q.links.self === lastUsedAnywhereQueueUrl) {
          printerHasLastUsedQueue = true;      // if printer is found and queue is matched set last used printer and queue
          q.isLastUsedQueue = true;
          q.isFavorite = true;
          this.lastUsedAnywhereQueue.set(q);
        }
      });
      if (!printerHasLastUsedQueue) { // if last used queue is not on the printer
        this.logger.info(methodName + 'LastUsedQueue not present on printer');
        this.resetPrinterAndQueue();
      }
    } else {
      this.logger.info(methodName + 'lastUsedAnywhereQueue() === null');
      this.resetPrinterAndQueue();
    }
  }

  public resetPrinterAndQueue(): void {
    this.logger.info('resetPrinterAndQueue()');
    this.lastUsedAnywhereQueue.set(null);
    this.selectedPrinterUrl.set(null);
    this.userService.removeLastUsedPrinterAndQueue()
    .subscribe(() => {
      this.userService.refreshCurrentUser()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.selectedPrintJobs.set([]);
        this.selectedQueue = null;
        this.scanning.set(false);
        if (this.appIsActive()) {
         console.log('## refreshPrintJobsAndPrinters() - resetPrinterAndQueue()');
          this.refreshPrintJobsAndPrinters().subscribe();
        }
      });
    });
  }

  // ############### /INIT ###############################################################################

  private isAllSelected(printer: IPrinter): void { // hasValidSelectedPrintJobs(), selectAllToggle(), selectPrintJob()
    this.logger.info('isAllSelected()');
    const printerJobList = [];
    const selectedPrintJobs = [];
    this.printJobList().forEach(pj => {
      if (pj.links.printer === printer.links.self) {
        printerJobList.push(pj);
      }
    });

    printerJobList.forEach(pj => {
      if (pj.options.isSelected) {
        selectedPrintJobs.push(pj);
      }
    });

    if (printerJobList.length === selectedPrintJobs.length && selectedPrintJobs.length > 0) {
      printer.checked = true;
    } else {
      printer.checked = false;
    }

    this.showPrinterSelectWindow();
  }

  private showPrinterSelectWindow(): boolean { // trickered by isAllSelected(), shows the action button panel at the bottom of the page when an anywhere document has been selected
    this.logger.info('showPrinterSelectWindow()');
    for (let j = 0; j < this.printerList().length; j++) {
      for (let i = 0; i < this.selectedPrintJobs().length; i++) {
        if (this.printerList()[j].links.self === this.selectedPrintJobs()[i].links.printer) {
          if (this.printerList()[j].type === 'FREEDOM') {
            this.printAnywhere.set(true);
            return;
          }
        }
      }
    }
    this.printAnywhere.set(false);
  }

  public findPrinterForPrintJob(printJob: IPrintJob): IPrinter { // hasValidSelectedPrintJobs()
    this.logger.info('findPrinterForPrintJob()');
    let printer: IPrinter = null;
    for (let i = 0; i < this.printerList().length; i++) {
      if (this.printerList()[i].links.self === printJob.links.printer) {
        printer = this.printerList()[i];
      } else {
        this.printerList()[i].options.allowToPrint = false;
      }
    }
    return printer;
  }

  private syncSelectedPrinter(printer: IPrinter): void { // selectPrintJob() selectAllToggle()
    if (printer.type !== 'FREEDOM') {
      this.selectedPrinter = printer;
      this.selectedPrinterUrl.set(printer.links.self);
    }
  }

  private findPrintJobAndRemove(printJob: IPrintJob): void { // selectPrintJob()
    this.logger.info('findPrintJobAndRemove()');
    const printJobID = printJob.links.self;
    this.selectedPrintJobs.update(jobs => 
      jobs.filter(job => job.links.self !== printJobID)
    );
  }

  private resetPrinterAllowToPrintProperty(printerSelectedPrintJob: Number, printJob?: IPrintJob): void { // selectPrintJob() selectAllToggle()
    this.logger.info('resetPrinterAllowToPrintProperty()');
    _.forEach(this.printerList(), (printer) => {
      if (Number(printerSelectedPrintJob) > 0) {
        if (printJob && this.checkPrintJobOnPrinter(printer, printJob)) {
          printer.options.allowToPrint = true;
        } else {
          printer.options.allowToPrint = false;
        }
      } else {
        printer.options.allowToPrint = true;
      }
    });
  }

  private checkPrintJobOnPrinter(printer: IPrinter, printJob: IPrintJob): boolean { // resetPrinterAllowToPrintProperty()
    this.logger.info('checkPrintJobOnPrinter()');
    if (printer.links.self === printJob.links.printer) {
      return true;
    }
    return false;
  }

  private async presentAlert(printer: IPrinter, releaseJobs: Array<any>, alertType: string): Promise<any> { // ALERT for unavailable selected printJobs
    this.logger.info('presentAlert() for deselect/releaseMatching/tooManyPrintJobs printJobs or cancelRelease');
    const dataPackage: any = {
      type: alertType,
      securePrintLevel: this.securePrintLevel(),
      releaseJobs: releaseJobs,
      errorParameters: this.tooManyPrintJobsInReleaseErrorParameters
    };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      componentProps: {dataPackage},
      cssClass: alertType,
      backdropDismiss: false
    });

    this.handleDismissedAlertDialog(popover, releaseJobs, printer);
  }

  private async handleDismissedAlertDialog(popover, releaseJobs, printer) {
    await popover.present();

    await popover.onDidDismiss()
    .then(popoverData => {
      if (popoverData.data === 'deselectPrintJobs') {
        this.handleDismissedDialogDeselectUnavailablePrintJobs(releaseJobs, printer);
      } else if (popoverData.data === 'releaseMatchingPrintJobs') {
        this.handleDismissedDialogReleaseMatchingPrintJobs(releaseJobs);
      } else if (popoverData.data === 'cancelRelease') {
        this.logger.info('onDidDismiss() cancelRelease');
        this.concludeScan();
        this.selectedQueue = null;
        this.scanning.set(false);
        if (this.appIsActive()) {
          console.log('##refreshPrintJobsAndPrinters() - cancelRelease()');
          this.refreshPrintJobsAndPrinters().subscribe();
        }
      } else if (popoverData.data === 'unMatchedPrinter') {
        this.concludeScan();
        this.selectedPrintJobs.set([]);
        this.selectedQueue = null;
        this.scanning.set(false);
        console.log('## refreshPrintJobsAndPrinters() - unMatchedPrinter()');
        this.refreshPrintJobsAndPrinters().subscribe();
      } else if (popoverData.data === 'tooManyPrintJobSelected') {
        if (popoverData.role === 'ok') {
          this.selectPrintJobsToLimit(printer); // Select only allowed amount of PJ's
        } else {
          printer.checked = false;
          this.selectAllToggle(printer);
        }
      }
    });
  }

  private handleDismissedDialogDeselectUnavailablePrintJobs(releaseJobs, printer) {
    this.logger.info('onDidDismiss() deselectPrintJobs');
    this.printJobsToAdd = [];
    releaseJobs.forEach((resourcePj) => {
      if (resourcePj.errors.length !== 0) {
        this.printJobList().forEach(pj => { // update UI deselecting unavailable PJ's
          if (resourcePj.jobId === pj.links.self) {
            pj.options.isSelected = false;
            pj.options.resourceErrors = [];
            this.findPrintJobAndRemove(pj);
          }
        });
      } else {
        this.printJobsToAdd.push(resourcePj.jobId);
      }
    });
    printer.options.isAllPrintJobsSelected = false;
    this.isAllSelected(printer);
    this.updatePrintJobOnRelease(printer);
    this.resetPrinterAllowToPrintProperty(this.selectedPrintJobs().length, this.selectedPrintJobs().length > 0 ? this.selectedPrintJobs()[0] : null);
  }

  private handleDismissedDialogReleaseMatchingPrintJobs(releaseJobs) {
    this.logger.info('onDidDismiss() releaseMatchingPrintJobs');
    this.selectedPrintJobs.set([]);
    releaseJobs.forEach((resourcePj) => {
      if (resourcePj.errors.length === 0) {
        this.printJobList().forEach(pj => { // update UI deselecting unavailable PJ's
        if (resourcePj.jobId === pj.links.self) {
            this.selectedPrintJobs.update(jobs => [...jobs, pj]);
            pj.options.isSelected = true;
          }
        });
      }
    });
    if (this.selectedPrintJobs().length > 0) {
      this.updateReleaseResourceBeforeRelease(this.selectedPrinter, this.selectedPrintJobs(), this.selectedQueue); // RELEASE MATCHING PRINT JOBS
    } else {
      this.printJobsToAdd = [];
      this.scanning.set(false);
      this.concludeScan();
      if (this.appIsActive()) {
        console.log('## refreshPrintJobsAndPrinters - handleDismissedDialogReleaseMatchingPrintJobs() no selected PrintJobs');
        this.refreshPrintJobsAndPrinters().subscribe();
      }
    }
  }

  // printerUrl is the printer queueUrl was derived from, so createRelease() does not fall back to a
  // this.selectedPrinter the queue may not belong to.
  private handleExpiredRelease(error, queueUrl?: string, printerUrl?: string) {
    if (error.status === 400 && error.error.errorCode === 'PATCHING_FINISHED_RELEASE') {
      this.logger.info('handleExpiredRelease()');
      this.dialogService.showLoadingSpinnerDialog('handleExpiredRelease()').subscribe(() => {
        this.createRelease(queueUrl, false, printerUrl);
      });
    } else {
      this.errorService.handleHttpClientResponseError(error, 'PATCH', '[ReleaseResourceService] updatePrintJobsOnRelease()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    }
  }

  private selectPrintJobsToLimit(printer: IPrinter) {
    this.logger.info('selectPrintJobsToLimit()');
    const printJobLimitString = this.tooManyPrintJobsInReleaseErrorParameters[0];
    const printJobLimitNumber = parseInt(printJobLimitString, 10);
    
    if (this.selectedPrintJobs().length > printJobLimitNumber) {
      // Get jobs to keep and deselect using spread
      const [jobsToKeep, jobsToDeselect] = [
        this.selectedPrintJobs().slice(0, printJobLimitNumber),
        this.selectedPrintJobs().slice(printJobLimitNumber)
      ];
      
      // Update UI state for deselected jobs
      jobsToDeselect.forEach(pj => {
        pj.options.isSelected = false;
      });
      
      // Update signal with new array reference containing only jobs to keep
      this.selectedPrintJobs.set([...jobsToKeep]);
    }
  
    this.isAllSelected(printer);
    this.updatePrintJobOnRelease(printer);
  }

  // ############### VIEW TRIGGERED ###############################################################################

  public goToReleaseHistoryPage(): void {
    this.logger.info('(click) goToReleaseHistoryPage()');
    this.navCtrl.navigateForward('/release-history');
  }

  public selectPrintJob(printJob: IPrintJob, printer: IPrinter): void { // VIEW
    this.logger.info('(click) selectPrintJob()');
    this.eventService.startTokenExpireCountDown();

    if (printer.options.allowToPrint) {
      // Keep selectedPrinter aligned with the active non-anywhere selection, so createRelease() doesn't use a stale printer after cross-queue releases.
      this.syncSelectedPrinter(printer);

      printJob.options.isSelected = !printJob.options.isSelected;
      const printJobUrl = printJob.links.self;

      if (printJob.options.isSelected) {
        this.selectedPrintJobs.update(jobs => [...jobs, printJob]);
      } else {
        this.findPrintJobAndRemove(printJob);
        printJob.options.resourceErrors = [];
      }

      if (this.debouncerRunning()) {
        clearTimeout(this.debounceTimer); // Clear timer
      } else {
        this.debouncerRunning.set(true);
      }
      this.setDebounceTime(printer);

      this.isAllSelected(printer);
      this.resetPrinterAllowToPrintProperty(this.selectedPrintJobs().length, printJob);
    }
  }

/////////////////////////////////////////////////////////////
////////////////////// SET DEBOUNCE TIME ////////////////////
/////////////////////////////////////////////////////////////

  public setDebounceTime(printer: IPrinter): any {
    this.logger.info('setDebounceTime()');
    this.debounceTimer = setTimeout(() => {
      this.dialogService.hideLoadingSpinnerDialog('setDebounceTime()');
      this.updatePrintJobOnRelease(printer);
    }, 800);
  }

/////////////////////////////////////////////////////////////
/////////// UPDATE PRINT JOBS ON RELEASE RESOURCE ///////////
/////////////////////////////////////////////////////////////

  public updatePrintJobOnRelease(printer: IPrinter) {
    const methodName = 'updatePrintJobOnRelease() ';
    this.logger.info(methodName);
    let queueUrl: string;
    let printerUrl: string;
    if (printer.type === 'FREEDOM') {
      queueUrl = this.lastUsedAnywhereQueue() ? this.lastUsedAnywhereQueue().links.self : null;
      printerUrl = this.selectedPrinter ? this.selectedPrinter.links.self : null;
    } else {
      queueUrl = this.selectedPrintJobs().length > 0 ? this.selectedPrintJobs()[0].links.queue : null;
      printerUrl = printer.links.self;
    }
    // The two branches take the queue and the printer from different places, so check the pair
    // here. Resolve a replacement rather than dropping the queue: updatePrintJobsOnRelease() emits
    // the /printerQueue op only with *both* values, so a null queue sends jobs ops alone and leaves
    // the release on whatever printer it held — a silent wrong-printer release.
    if (queueUrl && !this.queueUrlBelongsToPrinter(queueUrl, printerUrl)) {
      const rejectedQueueUrl: string = queueUrl;
      const printerForQueue: IPrinter = printer.type === 'FREEDOM' ? this.selectedPrinter : printer;
      queueUrl = this.resolveReleaseQueueUrl(null, this.selectedPrintJobs(), printerForQueue);
      this.logger.info(methodName + 'queue was not on the printer - '
        + JSON.stringify({ replaced: !!queueUrl }));
      this.healAnywhereQueueDrift(rejectedQueueUrl, queueUrl);
      if (!queueUrl) {
        // The op is suppressed and the release keeps the pair it had. Sending the printer alone is
        // not an option: unlike a create, a PATCH omitting `printerqueue` is rejected with
        // 422 API_INVALID_PATCH (verified against devenv).
        this.logger.warn(methodName + 'no queue belongs to the printer - release keeps its current printer/queue');
      }
    }

    this.printJobsToAdd = []; // reset print jobs to add before setting them in order to avoid duplicates
    this.selectedPrintJobs().forEach(pj => { // add all selected print job id's to addPrintJobs array
      this.printJobsToAdd.push(pj.links.self);
    });

    // If releaseResource is null (e.g., after releasing documents), create a new one
    if (!this.releaseResource) {
      this.logger.info(methodName + 'releaseResource is null, creating new release');
      this.createRelease(queueUrl, undefined, printerUrl);
      return;
    }

    // Cancel any prior in-flight create/update; cancelling fires its finalize synchronously (debouncerRunning -> false), so re-assert debouncerRunning=true for this replacement request.
    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(true);

    this.dialogService.showLoadingSpinnerDialog('updatePrintJobOnRelease()');
    let recoveryStarted = false;
    this.releaseResourceService.updatePrintJobsOnRelease(this.printJobsToAdd, printerUrl, queueUrl, this.releaseResource.resourceId)
    // finalize re-enables the Print button even on error or a concurrent cancel/unsubscribe, else debouncerRunning stays stuck true.
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => { if (!recoveryStarted) { this.debouncerRunning.set(false); } }))
    .subscribe((response) => {
      this.dialogService.hideLoadingSpinnerDialog('updatePrintJobOnRelease()');
      this.logger.info(methodName + '- ReleaseID: ' + response['resourceId']);
      this.releaseResource = response;
      const releaseJobs = response.releaseJobs;

      this.debouncerRunning.set(false);
      this.unavailablePrintJobs.set(false);

      this.setPrinterAccessError(response.printerQueue);
      this.setReleaseJobsErrors(releaseJobs);

      this.printJobsToAdd = [];
      this. checkIfAllSelected(printer, releaseJobs);
      this.checkSecurePrintLevel();
    }, (error) => {
      this.dialogService.hideLoadingSpinnerDialog('updatePrintJobOnRelease()');
      this.logger.info('updatePrintJobsOnRelease() httpErrorResponse.status: ' + error.status);
      if (error.status === 422 && error.error.errorCode === 'TOO_MANY_PRINT_JOBS_IN_RELEASE') {
        this.tooManyPrintJobsInReleaseErrorParameters = error.error.parameters;
        this.presentAlert(printer, null, 'tooManyPrintJobSelected');
      } else {
        recoveryStarted = error?.status === 400 && error?.error?.errorCode === 'PATCHING_FINISHED_RELEASE';
        this.handleExpiredRelease(error, queueUrl, printerUrl); // UpdatePrintJobsOnRelease
      }
    });
  }

  // Pushes the selected printer/queue onto the release resource so a later "start" prints to the
  // printer the user picked; creates a fresh resource if it was cleared. The queue is
  // lastUsedAnywhereQueue when it sits on that printer, otherwise a substitute — which can be the
  // print job's own queue.
  private updateReleaseResourceForSelectedPrinter(): void {
    const methodName = 'updateReleaseResourceForSelectedPrinter() ';
    this.logger.info(methodName);

    const printerUrl: string = this.selectedPrinter ? this.selectedPrinter.links.self : null;
    const anywhereQueueUrl: string = this.lastUsedAnywhereQueue() ? this.lastUsedAnywhereQueue().links.self : null;
    // As in updatePrintJobOnRelease(): substitute rather than drop, since a null queue suppresses
    // the /printerQueue op and leaves the release on its previous printer.
    let queueUrl: string = anywhereQueueUrl;
    if (queueUrl && !this.queueUrlBelongsToPrinter(queueUrl, printerUrl)) {
      queueUrl = this.resolveReleaseQueueUrl(null, this.selectedPrintJobs(), this.selectedPrinter);
      this.logger.info(methodName + 'queue was not on the printer - '
        + JSON.stringify({ replaced: !!queueUrl }));
      this.healAnywhereQueueDrift(anywhereQueueUrl, queueUrl);
      if (!queueUrl) {
        this.logger.warn(methodName + 'no queue belongs to the printer - release keeps its current printer/queue');
      }
    }
    const printJobUrls: Array<string> = this.selectedPrintJobs().map(pj => pj.links.self);

    if (!this.releaseResource) {
      this.logger.info(methodName + 'releaseResource is null, creating new release');
      this.createRelease(queueUrl, undefined, printerUrl);
      return;
    }

    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(true);

    let recoveryStarted = false;
    this.releaseResourceService.updatePrintJobsOnRelease(printJobUrls, printerUrl, queueUrl, this.releaseResource.resourceId)
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => { if (!recoveryStarted) { this.debouncerRunning.set(false); } }))
    .subscribe((response) => {
      this.logger.info(methodName + '- ReleaseID: ' + response['resourceId']);
      this.releaseResource = response;
      // Reset unavailablePrintJobs before recomputing (only setReleaseJobsErrors sets it true), or it stays stuck true and disables Print — mirrors createRelease()/updatePrintJobOnRelease().
      this.unavailablePrintJobs.set(false);
      this.setPrinterAccessError(response.printerQueue);
      this.setReleaseJobsErrors(response.releaseJobs);
    }, (error) => {
      this.logger.info(methodName + 'updatePrintJobsOnRelease() httpErrorResponse.status: ' + error.status);
      recoveryStarted = error?.status === 400 && error?.error?.errorCode === 'PATCHING_FINISHED_RELEASE';
      this.handleExpiredRelease(error, queueUrl, printerUrl);
    });
  }

  private checkIfAllSelected(printer, releaseJobs) {
    if (this.selectAllClicked) {
      this.selectAllClicked = false;
      if (this.unavailablePrintJobs()) {
        if (this.isCordova) {
          this.presentAlert(printer, releaseJobs, 'deselectPrintJobs');
        } else {
          if (!this.securePrintLevel() || this.securePrintLevel() === 'NONE') {
            this.presentAlert(printer, releaseJobs, 'deselectPrintJobs');
          }
        }
      }
    }
  }

  private checkSecurePrintLevel() {
    if (this.securePrintLevel() && this.securePrintLevel() === 'ON_DEVICE_RELEASE' && this.selectedPrintJobs().length > 0) {
      this.presentAlert(null, null, 'onDeviceRelease');
    } else if (this.securePrintLevel() && this.securePrintLevel() !== 'NONE' && !this.isCordova && this.selectedPrintJobs().length > 0) {
      this.presentAlert(null, null, 'securePrintEnabled');
    }
  }

  public selectAllToggle(printer: IPrinter): void { // TOGGLE ALL PJ
    this.logger.info('(click) selectAllToggle()');
    if (printer.options.allowToPrint) {
      this.syncSelectedPrinter(printer);

      this.dialogService.showLoadingSpinnerDialog('selectAllToggle()');
      this.selectAllClicked = true;

      if (this.debouncerRunning()) {
        clearTimeout(this.debounceTimer);
      }
      this.debouncerRunning.set(true);

      this.printJobsToAdd = [];
      this.selectedPrintJobs.set([]);

      this.handlePrinterToggleData(printer, printer.checked);
      this.isAllSelected(printer);
      this.setDebounceTime(printer);
    }
  }

  private handlePrinterToggleData(printer, printerChecked) {
    this.printJobList()
    .filter((printJob: IPrintJob) => printJob.links.printer === printer.links.self)
    .forEach((printJob: IPrintJob) => {
      printJob.options.isSelected = printerChecked;
      if (printerChecked) {
        this.selectedPrintJobs.update(jobs => [...jobs, printJob]);
      }
    });
    printer.options.isAllPrintJobsSelected = printerChecked;
  }

  public deleteSelectedPrintJobs (printJob?: IPrintJob) { // DELETE PJ
    this.logger.info('(click) deleteSelectedPrintJobs()');
    // Cancel any pending release-resource PATCH before deleting these jobs, so its stale response can't resurrect releaseResource afterward.
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(false);
    this.dialogService.showLoadingSpinnerDialog('deleteSelectedPrintJobs()').subscribe(() => {
      let printJobsToDelete: Array<IPrintJob> = [];

      if (printJob) {
        printJobsToDelete.push(printJob);
      } else {
        printJobsToDelete = this.selectedPrintJobs();
      }

      this.printJobService.deleteSelectedPrintJobs(printJobsToDelete)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.dialogService.hideLoadingSpinnerDialog('deleteSelectedPrintJobs()');
        const deletedJobUrls = printJobsToDelete.map(pj => pj.links.self);
        deletedJobUrls.forEach(url => this.pendingDeletedJobUrls.add(url));

        printJobsToDelete.forEach(pj => pj.options.isSelected = false);
        this.removeJobsFromLocalState(deletedJobUrls);

        this.releaseResource = null;

        this.resetPrintersAllowedToPrintProperty();
        if (this.appIsActive()) {
          console.log('## refreshPrintJobsAndPrinters() - deleteSelectedPrintJobs()');
          this.refreshPrintJobsAndPrinters().pipe(takeUntil(this.unSubscribe)).subscribe();
        }
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('deleteSelectedPrintJobs() - error');
        this.errorService.handleHttpClientResponseError(error, 'POST', 'deleteSelectedPrintJobs()')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    });
  }

  // ############### /VIEW TRIGGERED ###############################################################################

  // ################# MODAL #####################################################################################
  public async presentModal(event, target, modalType: string, fromPrinterList, printJobs) {
    const methodName = 'updatePrintJobOnRelease()';
    this.logger.info('(click) presentModal() ' + modalType);
    if (printJobs) {
      target.printJobs = printJobs;
    }

    const dataPackage: any = {
      target: target,
      printer: target,
      cssClass: modalType
    };

    const infoModal = await this.modalCtrl.create({
      component: ModalComponent,
      componentProps: {dataPackage},
      cssClass: modalType
    });

    await infoModal.present();
    this.handleModalDismissed(infoModal, modalType, fromPrinterList);
  }

  private async handleModalDismissed(infoModal, modalType, fromPrinterList) {
    await infoModal.onDidDismiss()
    .then(modalData => {
      this.logger.info(modalType + ' onDidDismiss() ' + modalData);
      if (modalType === 'printerInfo' && fromPrinterList) {
        this.presentPopover(event, 'select-printer-menu', this.printerList);
      } else if (modalType === 'selectPrintQueue') {
        // A backdrop tap, swipe-down or Android back button dismisses with no data at all, which
        // must conclude the scan the same as an explicit cancel — this modal has no queue to fall
        // back on otherwise.
        if (!modalData.data || modalData.data === 'cancelRelease') {
          this.concludeScan();
          this.scanning.set(false);
          if (this.appIsActive()) {
            console.log('## refreshPrintJobsAndPrinters() - cancelRelease()');
            this.refreshPrintJobsAndPrinters().subscribe();
          }
        } else {
          this.dialogService.showLoadingSpinnerDialog('modalDismissed->updateReleaseresourceBeforeRelease()').subscribe(() => {
            this.selectedQueue = modalData.data.queue;
            this.updateReleaseResourceBeforeRelease(modalData.data.printer, modalData.data.printJobs, this.selectedQueue); // SELECT QUEUE
          });
        }
      }
    });

  }
  // ################# /MODAL #####################################################################################

  // ################# POPOVER #####################################################################################
  public async presentPopover(myEvent, popoverType: string, data): Promise<void> {
    this.logger.info('(click) presentPopover() ' + popoverType);

    if(this.dataIsLoaded) {

      if (popoverType === 'select-printer-menu') { // to show list of favorite queues
        this.handleSelectPrinterMenuData(popoverType, myEvent);
      } else {
        if (popoverType === 'printJobMenu') {
          const dataPackage: any = {
            type: popoverType,
            printJob: data
          };

          const popover = await this.popoverCtrl.create({
            component: PopoverComponent,
            componentProps: {dataPackage},
            cssClass: popoverType
          });

          await popover.present();
          this.handlePopoverDismissed(popover, popoverType);
        }
      }
    }
  }

  private handleSelectPrinterMenuData(popoverType, myEvent) {
    this.logger.info('handleSelectPrinterMenuData()');
    if (!this.releaseResource) {
      this.logger.info('handleSelectPrinterMenuData() - releaseResource is null, rebuilding');
      this.createRelease();
      return;
    }
    const matchingQueuesUrl: string = this.releaseResource.matchingQueues;
    const lastUsedAnywhereQueueUrl: string = this.lastUsedAnywhereQueue() ? this.lastUsedAnywhereQueue().links.self : null ;

    if (this.favoriteQueueUrls.length !== 0) {
      this.dialogService.showLoadingSpinnerDialog('handleSelectPrinterMenuData()').subscribe(() => {
        this.releaseResourceService.getAvailablePrintersList(matchingQueuesUrl, this.favoriteQueueUrls, 0, 500, null, [])
        .pipe(takeUntil(this.unSubscribeMatchingQueues))
        .subscribe( async (response) => {
          const availableFavoritePrinters: Array<IPrinter> = response.printers;
          availableFavoritePrinters.forEach(p => {
            p.queues.forEach(q => {
              q.isFavorite = true;
              if (q.links.self === lastUsedAnywhereQueueUrl) {
                q.isLastUsedQueue = true;
              }
            });
          });

          this.unSubscribeMatchingQueues.next();
          this.unSubscribeMatchingQueues.complete();
          if (availableFavoritePrinters.length !== 0) {
              const dataPackage: any = {
                type: popoverType,
                printerList: availableFavoritePrinters,
                printJobs: this.selectedPrintJobs(),
                originalEvent: myEvent
              };

              const popover = await this.popoverCtrl.create({
                component: PopoverComponent,
                componentProps: {dataPackage},
                cssClass: popoverType,
                animated: true
              });

              this.dialogService.hideLoadingSpinnerDialog('handleSelectPrinterMenuData()');
              await popover.present();
              this.handlePopoverDismissed(popover, popoverType);
            } else {
             this.dialogService.hideLoadingSpinnerDialog('ERROR - handleSelectPrinterMenuData()');
            this.navCtrl.navigateForward(['/printer-list'], {queryParams: { resource: this.releaseResource }});
          }
        }, (error) => {
          this.errorService.handleHttpClientResponseError(error, 'POST', 'handleSelectPrinterMenuData()')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
          // It is ok to see matching queues for an expired release, a new release will be created, when a release is started
        });
      });
    } else {
      this.navCtrl.navigateForward(['/printer-list'], {queryParams: { resource: this.releaseResource }});
    }
  }

  private async handlePopoverDismissed(popover, popoverType) {
    await popover.onDidDismiss()
    .then(popoverData => {
      this.logger.info(popoverType + ' onDidDismiss()');
      console.log('popoverData', popoverData);
      const popData = popoverData.data
      if (popData && popData.event) {
        switch (popData.event) {
          case 'showPrintJobInfo':
            this.presentModal(popData.originalEvent, popData.target, popData.event, true, null);
          break;
          case 'deletePrintJob':
            if (this.printJobsToAdd.length === 0) {
              this.deleteSelectedPrintJobs(popData.target);
            }
          break;
          case 'print':
            this.selectedPrintJobs.set(popData.printJobs);
            const selectedPrinter = popData.printer;
            const selectedQueue = popData.target;
            this.userService.setLastUsedPrinter(selectedPrinter) // set last used printer
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(() => {
              this.logger.info('releaseSelectedPrintJobs() - setting lastUsedQueue: ' + selectedQueue.links.self);
              this.userService.setLastUsedQueue(selectedQueue)
              .pipe(takeUntil(this.unSubscribe))
              .subscribe(() => {
                if (this.appIsActive()) {
                  this.selectedPrinter = selectedPrinter;
                  this.selectedPrinterUrl.set(selectedPrinter.links.self);
                  this.checkForLastUsedQueue();

                  // Sync the release resource with the newly chosen printer/queue — otherwise only lastUsedPrinter/lastUsedQueue update and Start still prints to the old printer.
                  this.updateReleaseResourceForSelectedPrinter();

                  // Store selected print job URLs before refresh
                  const selectedPrintJobUrls = this.selectedPrintJobs().map(pj => pj.links.self);
                  
                  this.refreshPrintJobsAndPrinters().subscribe(() => {
                    // Restore selected state after refresh
                    if (selectedPrintJobUrls.length > 0) {
                      const updatedSelectedJobs: Array<IPrintJob> = [];
                      this.printJobList().forEach(printJob => {
                        if (selectedPrintJobUrls.includes(printJob.links.self)) {
                          printJob.options.isSelected = true;
                          updatedSelectedJobs.push(printJob);
                        }
                      });
                      this.selectedPrintJobs.set(updatedSelectedJobs);
                    }
                  });
                }
              });
            });
          break;
          case 'printerInfo':
            this.presentModal(popData.originalEvent, popData.target, popData.event, true, null);
          break;
          case 'showPrintQueuesListPage':
            this.navCtrl.navigateForward(['/printer-list'], {queryParams: { resource: this.releaseResource }});
          break;
        }
      }
      if (popoverData === null) {
        this.favoriteQueueUrls = [];
        if (this.appIsActive()) {
          console.log('## refreshPrintJobsAndPrinters() - handlePopoverDismissed() - popoverData === null');
          this.refreshPrintJobsAndPrinters().subscribe();
        }
      }
    });
  }
  // ################# /POPOVER #####################################################################################

  // ######################### RELEASE PRINTJOBS #############################################################################################################################################
  private releaseSelectedPrintJobs(selectedPrinter: IPrinter, selectedPrintJobs: Array<IPrintJob>, selectedQueue, retryOnExpiredRelease: boolean = true): void {
    const methodName = 'releaseSelectedPrintJobs() ';
    this.logger.info(methodName);
    this.debouncerRunning.set(true);
    if (!this.releaseResource) {
      if (retryOnExpiredRelease) {
        this.logger.info(methodName + 'releaseResource is null — recreating before release');
        this.recreateReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
      } else {
        this.logger.info(methodName + 'releaseResource still null after recreate — giving up');
        this.debouncerRunning.set(false);
        this.scanning.set(false);
        this.concludeScan();
        this.dialogService.showTranslatedMessageDialog('Something went wrong, please try to release again');
      }
      return;
    }
    // A start needs both a printer and a queue on the resource. Without them it fails with
    // STARTING_RELEASE_MISSING_VALUES *after* the backend has moved the release to RELEASED, so it
    // can never be started again and the job never prints. Patch the selection on first.
    if (!this.releaseResource.printerQueue?.selectedPrinter || !this.releaseResource.printerQueue?.selectedQueue) {
      this.logger.info(methodName + 'release resource has no printer/queue — patching before release');
      this.addPrinterQueueToReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue, retryOnExpiredRelease);
      return;
    }
    this.startReleaseOnResource(selectedPrinter, selectedPrintJobs, selectedQueue, retryOnExpiredRelease);
  }

  // Starts the release on a resource already known to carry a printer and a queue, so the recovery
  // paths can start without re-entering the guard that sent them there.
  private startReleaseOnResource(
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue,
    retryOnExpiredRelease: boolean
  ): void {
    const methodName = 'startReleaseOnResource() ';
    this.logger.info(methodName);
    const printJobUrls: Array<string> = selectedPrintJobs.map(pj => pj.links.self);
    this.logsService.logReleaseItems(selectedPrinter, selectedQueue, printJobUrls, this.releaseResource.resourceId, this.printJobsPageType);
    if (selectedPrinter) {
      this.releasePrintService.releaseSelectedPrintJobs(selectedPrinter, selectedPrintJobs, selectedQueue, this.releaseResource.startRelease)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((response) => {
        this.logger.info(methodName + `release success - appIsActive=${this.appIsActive()} scanning=${this.scanning()}`);
        this.releaseResource = null;
        this.selectedPrintJobs.set([]);
        this.selectedQueue = null;
        this.scanning.set(false);
        this.concludeScan();

        _.forEach(this.printerList(), (printer) => {
          printer.checked = false;
        });

        this.setFavoriteQueueAndRefreshPage(selectedQueue);

      },(error) => {
        this.scanning.set(false);
        this.debouncerRunning.set(false);
        if (error.status === 400 && error.error.errorCode === 'PATCHING_FINISHED_RELEASE') {
          this.logger.info(methodName + 'ReleaseResource expired');
          if (retryOnExpiredRelease) {
            // Release resource expired between printer selection and Print; recreate it with the same jobs/printer/queue and retry.
            this.recreateReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
          } else {
            this.debouncerRunning.set(false);
            this.logger.info(methodName + 'release still expired after recreating release resource');
            this.concludeScan();
            this.dialogService.showTranslatedMessageDialog('Something went wrong, please try to release again');
          }
        } else if (error.status === 400 && error?.error?.errorCode === 'STARTING_RELEASE_INVALID_STATE' && retryOnExpiredRelease) {
          // PATCHING_FINISHED_RELEASE above is what a *PATCH* to a finished release returns; a
          // *start* on one the server has abandoned returns INVALID_STATE instead.
          this.logger.info(methodName + 'release is no longer startable - checking server state');
          this.recoverFromUnstartableRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
        } else {
          this.debouncerRunning.set(false);
          this.logger.info(methodName + 'release failed');
          this.concludeScan();
          this.errorService.handleHttpClientResponseError(error, 'POST', '[ReleasePrintService] releaseSelectedPrintJobs()')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        }
      });
    } else {
      this.scanning.set(false);
      this.concludeScan();
      this.logger.info(methodName + 'selectedPrinter is missing');
      throw "print: selectedPrinter is missing";
    }
  }

  // Drops the local reference to a release the server has aborted, so the next Print rebuilds a
  // live one instead of starting a dead one (releaseSelectedPrintJobs() recreates when it is null).
  // The selection is untouched, and a false positive costs only an extra create — hence discarding.
  private discardAbortedReleaseResource(releaseUrl: string): void {
    const methodName = 'discardAbortedReleaseResource() ';
    this.logger.info(methodName);
    if (!this.releaseResource) {
      this.logger.info(methodName + 'no release resource held - nothing to discard');
      return;
    }
    // Ignore only when the event names a *different* release: the payload does not always carry
    // the url, and a missing field must not suppress the recovery.
    if (releaseUrl && releaseUrl !== this.releaseResource.resourceId) {
      this.logger.info(methodName + 'event is for another release - ignoring');
      return;
    }
    this.logger.info(methodName + 'release aborted by the server - discarding resource - '
      + JSON.stringify({ resourceId: this.releaseResource.resourceId }));
    this.releaseResource = null;
  }

  // Backstop when a start is rejected with STARTING_RELEASE_INVALID_STATE, reached only if the
  // Pusher abort was missed. The same error covers a release the server aborted and one that has
  // already run (e.g. from another device) — the body names the state the server wants, never the
  // one the release is in — so the release is read first rather than recreated blindly.
  //
  // The read asks px:releaseStartResult, not `state`: job-service commits a release to RELEASED
  // *before* validating the start, so RELEASED covers both a start that dispatched work and one
  // that failed afterwards. The start result says whether a task reached a workstation.
  private recoverFromUnstartableRelease(
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue
  ): void {
    const methodName = 'recoverFromUnstartableRelease() ';
    this.logger.info(methodName);
    const resourceId: string = this.releaseResource ? this.releaseResource.resourceId : null;
    if (!resourceId) {
      this.logger.info(methodName + 'no release resource to inspect - recreating');
      this.recreateReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
      return;
    }

    this.debouncerRunning.set(true);
    let recoveryStarted = false;
    this.releaseResourceService.getRelease(resourceId)
    .pipe(takeUntil(this.unSubscribe),
          finalize(() => { if (!recoveryStarted) { this.debouncerRunning.set(false); this.scanning.set(false); this.concludeScan(); } }))
    .subscribe((release: any) => {
      const state: string = release ? release['state'] : null;
      const startResultUrl: string = release?._links?.['px:releaseStartResult']?.href;
      this.logger.info(methodName + 'release read from server - '
        + JSON.stringify({ state: state, hasStartResult: !!startResultUrl }));

      if (startResultUrl) {
        recoveryStarted = true;
        this.recoverFromStartResult(startResultUrl, selectedPrinter, selectedPrintJobs, selectedQueue);
        return;
      }

      // No start result to ask. CREATING and ABORTED are the only states where the jobs certainly
      // never went out; anything else counts as possibly printed, since a double print is worse
      // than a Print the user can press again.
      if (state === 'CREATING' || state === 'ABORTED') {
        recoveryStarted = true;
        this.recreateFromDeadRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
      } else {
        this.logger.info(methodName + 'no start result and state is not recoverable - not recreating');
        this.reportReleaseAlreadyStarted();
      }
    }, (error) => {
      // Without the state a double print cannot be ruled out, so report the failure instead of
      // guessing.
      this.logger.info(methodName + 'could not read release state - ' + JSON.stringify({ status: error?.status }));
      this.errorService.handleHttpClientResponseError(error, 'GET', '[ReleaseResourceService] getRelease()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  // Asks the release's start result whether any work actually reached a workstation, and recovers
  // only when nothing did.
  private recoverFromStartResult(
    startResultUrl: string,
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue
  ): void {
    const methodName = 'recoverFromStartResult() ';
    this.logger.info(methodName);
    this.debouncerRunning.set(true);
    let recoveryStarted = false;
    this.releaseResourceService.getReleaseStartResult(startResultUrl)
    .pipe(takeUntil(this.unSubscribe),
          finalize(() => { if (!recoveryStarted) { this.debouncerRunning.set(false); this.scanning.set(false); this.concludeScan(); } }))
    .subscribe((startResult: any) => {
      const dispatched: boolean = this.releaseDispatchedWork(startResult);
      this.logger.info(methodName + 'start result read - ' + JSON.stringify({ dispatched: dispatched }));
      if (dispatched) {
        this.reportReleaseAlreadyStarted();
        return;
      }
      recoveryStarted = true;
      this.recreateFromDeadRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
    }, (error) => {
      // Without the start result a double print cannot be ruled out.
      this.logger.info(methodName + 'could not read the start result - ' + JSON.stringify({ status: error?.status }));
      this.errorService.handleHttpClientResponseError(error, 'GET', '[ReleaseResourceService] getReleaseStartResult()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  // True when the start created work. The *existence* of a task is the test, not its flags:
  // hasTriedSendingTask / sentSuccessfully / acknowledgement are filled in later as the workstation
  // is contacted, so reading them would call a not-yet-sent task "nothing dispatched" and recreate
  // over the top of it. An empty list is the only shape seen for a start that dispatched nothing.
  private releaseDispatchedWork(startResult: any): boolean {
    const methodName = 'releaseDispatchedWork() ';
    const tasks: any = startResult?._embedded?.releaseTask;
    if (!Array.isArray(tasks)) {
      // "Cannot tell" is not evidence that nothing went out, so assume dispatched rather than
      // inverting the guard if the response shape ever changes.
      this.logger.info(methodName + 'no releaseTask projection - assuming dispatched');
      return true;
    }
    return tasks.length > 0;
  }

  // The release is dead but nothing printed: drop it and rebuild from the current selection.
  private recreateFromDeadRelease(
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue
  ): void {
    this.releaseResource = null;
    this.recreateReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
  }

  // The jobs already went out. Tell the user, then clear the selection and refetch so the printed
  // jobs leave the list; otherwise every further press of Print repeats the same round-trip.
  // releaseResource deliberately keeps pointing at the spent release — nulling it is what would let
  // the next Print recreate and double-print. A later PATCH gets PATCHING_FINISHED_RELEASE and the
  // recovery there replaces it.
  private reportReleaseAlreadyStarted(): void {
    const methodName = 'reportReleaseAlreadyStarted() ';
    this.logger.info(methodName + 'release already started - not recreating');
    this.concludeScan();
    this.dialogService.showTranslatedMessageDialog('ReleaseAlreadyStarted');
    this.selectedPrintJobs.set([]);
    _.forEach(this.printerList(), (printer) => {
      printer.checked = false;
    });
    this.refreshPrintJobsAndPrinters().pipe(takeUntil(this.unSubscribe)).subscribe();
  }

  // Recovery for a release resource that reached the Print action without a printer/queue: patch
  // the current selection onto it and start the release once the resource holds both. Re-enters
  // releaseSelectedPrintJobs() with retryOnExpiredRelease false, so a resource that still has no
  // queue after one patch is started as-is rather than patched again in a loop.
  private addPrinterQueueToReleaseAndRelease(
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue,
    retryOnExpiredRelease: boolean
  ): void {
    const methodName = 'addPrinterQueueToReleaseAndRelease() ';
    this.logger.info(methodName);

    const printerUrl: string = selectedPrinter ? selectedPrinter.links.self : null;
    const queueUrl: string = this.resolveReleaseQueueUrl(selectedQueue, selectedPrintJobs, selectedPrinter);

    // Nothing better to patch on, or the resource still had no queue after patching once. Start
    // anyway: this guard must never block a release that would have gone through without it, and
    // a start that does fail now reports its own error rather than a retry's.
    if (!printerUrl || !queueUrl || !retryOnExpiredRelease) {
      this.logger.info(methodName + 'cannot improve printer/queue on release - hasPrinter: ' + !!printerUrl
        + ', hasQueue: ' + !!queueUrl + ', retryOnExpiredRelease: ' + retryOnExpiredRelease + ' - starting as-is');
      this.startReleaseOnResource(selectedPrinter, selectedPrintJobs, selectedQueue, retryOnExpiredRelease);
      return;
    }

    const printJobUrls: Array<string> = selectedPrintJobs.map(pj => pj.links.self);
    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(true);
    let releaseStarted = false;
    this.releaseResourceService.updatePrintJobsOnRelease(printJobUrls, printerUrl, queueUrl, this.releaseResource.resourceId, this.scanningMethod)
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => { if (!releaseStarted) { this.debouncerRunning.set(false); this.scanning.set(false); this.concludeScan(); } }))
    .subscribe((response) => {
      this.logger.info(methodName + 'patched printer/queue onto release - ' + JSON.stringify({ resourceId: response['resourceId'] }));
      this.releaseResource = response;
      releaseStarted = true;
      this.releaseSelectedPrintJobs(selectedPrinter, selectedPrintJobs,
        this.queueForResolvedUrl(queueUrl, selectedQueue), false);
    }, (error) => {
      this.logger.info(methodName + 'failed to patch printer/queue onto release - ' + JSON.stringify({ status: error?.status }));
      if (error?.status === 400 && error?.error?.errorCode === 'PATCHING_FINISHED_RELEASE') {
        // An expired release is recoverable, and this is a Print path: it has to be recreated
        // *and* started. handleExpiredRelease() — which the two selection-change callers use —
        // only recreates, which is right for them and would leave this Print silently doing
        // nothing. recreateReleaseAndRelease() does both.
        releaseStarted = true;
        this.recreateReleaseAndRelease(selectedPrinter, selectedPrintJobs, selectedQueue);
        return;
      }
      this.errorService.handleHttpClientResponseError(error, 'PATCH', '[ReleaseResourceService] updatePrintJobsOnRelease()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  // The resolver returns a url, but ReleasePrintService persists the IQueue it is handed. So after a
  // substitution, passing the original object on would persist and pin the very queue the coherence
  // check rejected. Returns the queue matching the url actually used, or null — which only skips the
  // convenience update, where a wrong object corrupts it.
  private queueForResolvedUrl(queueUrl: string, selectedQueue: IQueue): IQueue {
    const methodName = 'queueForResolvedUrl() ';
    // Null for either absence: no coherent queue was sent, or the caller released without one on
    // purpose (a direct release). A lookup here would re-enable the write that null exists to skip,
    // because it usually succeeds — the resource's own queue is one of the candidates below.
    if (!queueUrl || !selectedQueue) {
      return null;
    }
    if (selectedQueue.links.self === queueUrl) {
      return selectedQueue;
    }
    const candidates: Array<IQueue> = [
      this.lastUsedAnywhereQueue(),
      this.selectedQueue,
      this.releaseResource?.printerQueue?.selectedQueue,
      ...(this.selectedPrinter?.queues ?? [])
    ];
    const matched: IQueue = candidates.find(queue => queue && queue.links.self === queueUrl);
    this.logger.info(methodName + JSON.stringify({ matched: !!matched }));
    return matched ?? null;
  }

  // Points lastUsedAnywhereQueue at the substitute the PATCH actually used. Left on the rejected
  // url, print() persists it as userAppSettings.lastUsedQueue against the printer released to, and
  // the next launch's checkForLastUsedQueue() cannot find it there and calls resetPrinterAndQueue()
  // — losing the Print Anywhere setting instead of correcting it.
  //
  // Only when the rejected url *was* the Anywhere queue: a direct job's queue is not a Print
  // Anywhere setting (docs/workflows/print-release.md §7.2), and the non-FREEDOM branch of
  // updatePrintJobOnRelease() resolves against exactly that.
  private healAnywhereQueueDrift(rejectedQueueUrl: string, resolvedQueueUrl: string): void {
    const methodName = 'healAnywhereQueueDrift() ';
    const anywhereQueue: IQueue = this.lastUsedAnywhereQueue();
    if (!resolvedQueueUrl || !anywhereQueue || anywhereQueue.links.self !== rejectedQueueUrl) {
      return;
    }
    // Only a queue that can be named as an object: the last resolver candidate is a bare url off
    // the print job, and what gets persisted is the IQueue.
    const substitute: IQueue = this.queueForResolvedUrl(resolvedQueueUrl, anywhereQueue);
    if (!substitute) {
      this.logger.info(methodName + 'resolved url matches no known queue - leaving the setting alone');
      return;
    }
    this.logger.info(methodName + JSON.stringify({ from: rejectedQueueUrl, to: resolvedQueueUrl }));
    this.lastUsedAnywhereQueue.set(substitute);
  }

  // Best queue available for the release, in order of specificity. When selectedPrinter is given,
  // candidates not on it are skipped so the pair sent to the server is coherent; callers that
  // cannot name a printer keep the unfiltered precedence.
  private resolveReleaseQueueUrl(selectedQueue: IQueue, selectedPrintJobs: Array<IPrintJob>, selectedPrinter?: IPrinter): string {
    const methodName = 'resolveReleaseQueueUrl() ';
    this.logger.info(methodName);
    const printerUrl: string = selectedPrinter ? selectedPrinter.links.self : null;
    const anywhereQueue: IQueue = this.lastUsedAnywhereQueue();

    // Every candidate is checked against the printer, so adding one can only turn a failed
    // resolution into a coherent pair, never an incoherent one. The two page-state candidates rank
    // below the Anywhere queue so they rescue a resolution that would fail rather than changing one
    // that succeeds.
    //
    // The Anywhere queue is offered only on the Anywhere path: lastUsedQueue is a Print Anywhere
    // setting (docs/workflows/print-release.md §7.2), and a direct job's queue is fixed at
    // submission, so a leftover Anywhere queue must never stand in for it — even on the same printer.
    const candidates: Array<{ source: string, url: string }> = [
      { source: 'the queue selected for this release', url: selectedQueue ? selectedQueue.links.self : null },
      { source: 'the Print Anywhere queue', url: this.printAnywhere() && anywhereQueue ? anywhereQueue.links.self : null },
      { source: 'the queue picked for the selected printer', url: this.selectedQueue ? this.selectedQueue.links.self : null },
      { source: 'the queue already on the release resource', url: this.releaseResource?.printerQueue?.selectedQueue ? this.releaseResource.printerQueue.selectedQueue.links.self : null },
      { source: 'the queue the print job was submitted to', url: selectedPrintJobs.length > 0 ? selectedPrintJobs[0].links.queue : null }
    ];

    const resolved = candidates.find(candidate => candidate.url && this.queueUrlBelongsToPrinter(candidate.url, printerUrl));
    if (!resolved) {
      this.logger.info(methodName + 'no queue belongs to the selected printer - releasing without one');
      return null;
    }
    this.logger.info(methodName + 'using ' + resolved.source);
    return resolved.url;
  }

  // The server answers 404 PRINT_QUEUE_NOT_FOUND for a pair it does not recognise as related. A
  // queue url is <tenant>/printers/<printerId>/queues/<queueId>, so a coherent pair is one where
  // the queue url sits under the printer url — no id parsing. The printer wins because it is what
  // the user picked; preferring the queue's printer would reintroduce the PR #444 regression.
  private queueUrlBelongsToPrinter(queueUrl: string, printerUrl: string): boolean {
    if (!printerUrl || !queueUrl) {
      return true;
    }
    // Accept anything not of the nested shape: a url that does not encode its printer cannot be
    // judged, and this guard must never block a release that would have gone through without it.
    if (!queueUrl.includes('/printers/') || !queueUrl.includes('/queues/')) {
      return true;
    }
    return queueUrl.startsWith(printerUrl + '/queues/');
  }

  // Fallback for a release resource that expired before the user released: rebuild it from the same
  // jobs/printer/queue (mirrors createRelease()) and start again. retryOnExpiredRelease is false on
  // the retry, so a repeated expiry cannot loop.
  private recreateReleaseAndRelease(selectedPrinter: IPrinter, selectedPrintJobs: Array<IPrintJob>, selectedQueue): void {
    const methodName = 'recreateReleaseAndRelease() ';
    this.logger.info(methodName);

    const releaseObject: object = {};
    // A direct release passes no queue, so fall back the same way the printer/queue recovery does.
    const queueUrl: string = this.resolveReleaseQueueUrl(selectedQueue, selectedPrintJobs, selectedPrinter);
    // Send the printer even with no queue resolved. Verified against devenv: a create carrying
    // printer + jobs is accepted and the server fills in the queue, so the release is startable.
    // Dropping the printer too leaves printerQueue null and the start fails with
    // STARTING_RELEASE_MISSING_VALUES.
    if (selectedPrinter) {
      releaseObject['printer'] = selectedPrinter.links.self;
      if (queueUrl) {
        releaseObject['queue'] = queueUrl;
      }
    }
    if (selectedPrintJobs.length !== 0) {
      releaseObject['jobs'] = selectedPrintJobs.map(pj => pj.links.self);
    }

    this.cancelPendingReleaseUpdate.next();
    this.debouncerRunning.set(true);
    let releaseStarted = false;
    this.releaseResourceService.createReleaseResource(releaseObject)
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => { if (!releaseStarted) { this.debouncerRunning.set(false); this.scanning.set(false); this.concludeScan(); } }))
    .subscribe((response) => {
      this.logger.info(methodName + 'recreated release - ReleaseID: ' + response['resourceId']);
      this.releaseResource = response;
      releaseStarted = true;
      this.releaseSelectedPrintJobs(selectedPrinter, selectedPrintJobs,
        this.queueForResolvedUrl(queueUrl, selectedQueue), false);
    }, (error) => {
      this.logger.info(methodName + 'failed to recreate expired release - status: ' + error?.status);
      this.errorService.handleHttpClientResponseError(error, 'POST', '[ReleaseResourceService] createReleaseResource()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  private refreshIfActive(reason: string): void {
    const methodName = 'refreshIfActive() ';
    this.logger.info(methodName + JSON.stringify({ reason, appIsActive: this.appIsActive() }));
    if (this.appIsActive()) {
      this.refreshPrintJobsAndPrinters().pipe(takeUntil(this.unSubscribe)).subscribe();
    }
  }

  private setFavoriteQueueAndRefreshPage(selectedQueue) {
    this.debouncerRunning.set(false);
    this.resetPrintersAllowedToPrintProperty();
    if (selectedQueue && this.favoriteQueueUrls.indexOf(selectedQueue.links.self) === -1) { // set the queue as favorite if it is not favorite already
      this.userService.changePrinterQueuePinStatus(selectedQueue.links.self, selectedQueue.isFavorite)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        () => this.refreshIfActive('changePrinterQueuePinStatus success'),
        () => this.refreshIfActive('changePrinterQueuePinStatus error'));
    } else {
      this.refreshIfActive('no selected queue');
    }
  }

  public print(): void { // VIEW
    this.logger.info('(click) print()');

    let selectedPrinter: IPrinter;
    let releaseQueue: IQueue;

    if (this.printAnywhere()) {
      selectedPrinter = this.selectedPrinter;
      releaseQueue = this.lastUsedAnywhereQueue();
    } else {
      const selectedPrinterUrl = this.selectedPrintJobs()[0].links.printer;
      const selectedPrinterFromUrl = this.printerList().filter(printer => printer.links.self === selectedPrinterUrl);
      selectedPrinter = selectedPrinterFromUrl[0];
      // A direct release must not record lastUsedPrinter/lastUsedQueue, which ReleasePrintService
      // keys off a null queue. Pass null rather than clearing lastUsedAnywhereQueue — clearing it
      // leaves the *next* Anywhere release without a queue, failing with MISSING_VALUES.
      releaseQueue = null;
    }

    this.releaseSelectedPrintJobs(selectedPrinter, this.selectedPrintJobs(), releaseQueue);
  }

  // ##################### /RELEASE PRINTJOBS ################################################################################

  // ##################### START SCANNING ################################################################################

  public async startScanning(event, popoverType): Promise<void> {
    this.logger.info('(click) startScanning()');
    // Which scanning method(s) available: if one, start it; if multiple, show selection.
    if (this.securePrintMethods.length === 1) {
      this.activateChosenScanningMethod(this.securePrintMethods[0]);
    } else if (this.securePrintMethods.length > 1) { // show popover of scanning choices

      const dataPackage: any = {
        type: popoverType,
        securePrintMethods: this.securePrintMethods,
        securePrintLevel: this.securePrintLevel()
      };

      const popover = await this.popoverCtrl.create({
        component: PopoverComponent,
        componentProps: {dataPackage},
        cssClass: 'choose-scanning-method'
      });

      await popover.present();

      await popover.onDidDismiss()
      .then((chosenScanningMethod) => {
        this.activateChosenScanningMethod(chosenScanningMethod.data);
      });

    } else {
      this.dialogService.showAlertDialog('NoScanningOptionsEnabled').subscribe();
    }
  }

  private activateChosenScanningMethod(chosenScanningMethod): void {
    this.logger.info('activateChosenScanningMethod():' + chosenScanningMethod);
    switch (chosenScanningMethod) {
      case 'QR':
        this.openQRScanner();
      break;
      case 'NFC':
       this.activateNfc();
      break;
    }
  }

  public checkActiveSecureLevel(): boolean {
    let disableScan: boolean = true;

    if (this.securePrintLevel() === 'NFC') {
      if (this.securePrintMethods.indexOf('NFC') > -1) {
        disableScan = false;
      }
    } else if (this.securePrintLevel() === 'QR') {
      if (this.securePrintMethods.indexOf('QR') > -1 || this.securePrintMethods.indexOf('NFC') > -1) {
        disableScan = false;
      }
    }

    return disableScan;
  }

// ##################### /START SCANNING ##############################################################################

// ##################### NFC SCANNING ################################################################################

  public activateNfc() {
    if (this.nfcReaderMode) {
      this.scanning.set(false);
      this.nfcReaderMode.unsubscribe();
      if (this.isAndroid) {
        this.setPreventNFCListener();
      }
    }
    this.broadcaster.broadcast('DISABLE_RESUME');
    this.scanNfcTag();
  }

  private scanNfcTag(): void {
    const methodName = 'scanNfcTag() ';
    this.logger.info(methodName);
    if (this.isAndroid) {
      console.log('## PlatformIsAndroid');
      this.scanWithAndroid();
    } else if (this.isIPhone) {
      this.scanTagWithIOS('scanTag')
      .subscribe((event) => {
          this.prepareScannedData(event);
      }, (error) => {
          this.logger.info(methodName + 'NFC scanTag() ERROR: ' + JSON.stringify(error));
          this.logger.info(methodName + 'Trying scanNdef() method');
          this.scanTagWithIOS('scanNdef')
          .subscribe((event) => {
              this.prepareScannedData(event);
          }, (error) => {
            this.logger.info(methodName + 'NFC scanNdef() ERROR: ' + JSON.stringify(error));
            this.logger.error(methodName + 'Not able to scan tag');
            this.dialogService.showAlertDialog('Not able to scan tag').subscribe();
          });
      });
    }
  }

  private scanTagWithIOS(scanMethod): Observable<any> {
    const methodName = 'scanTagWithIOS() ';
    this.logger.info(methodName);
    return new Observable((observer: Subscriber<any>) => {
      if (scanMethod === 'scanNdef') {
        this.nfc.scanNdef().then((event) => {
          this.logger.info(methodName + 'Scanned Ndef success');
          observer.next(event)
          observer.complete();
        }, (error) => {
          this.logger.info(methodName + 'Scanned Ndef failure');
          observer.error(error);
          observer.complete();
        });
      } else if (scanMethod === 'scanTag') {
        this.nfc.scanTag().then((event) => {
          this.logger.info(methodName + 'Scanned Tag success');
          observer.next(event)
          observer.complete();
        }, (error) => {
          this.logger.info(methodName + 'Scanned Tag failure');
          observer.error(error);
          observer.complete();
        });
      }
    });
  }

  private async scanWithAndroid() {
    const methodName = 'scanWithAndroid() ';
    this.logger.info(methodName);
    this.showScanReadyMessageAndroid();

    // handle Android scan
    const flags = this.nfc.FLAG_READER_NFC_A | this.nfc.FLAG_READER_NFC_V;
    this.nfcReaderMode = this.nfc.readerMode(flags).subscribe(
    (tag) => {
        console.log(tag);
        this.logger.info(methodName + 'canMakeReadOnly: ' + JSON.stringify(tag['canMakeReadOnly']));
        this.logger.info(methodName + 'isWritable: ' + JSON.stringify(tag['isWritable']));
        this.logger.info(methodName + 'maxSize: ' + JSON.stringify(tag['maxSize']));
        this.logger.info(methodName + 'tag type: ' + JSON.stringify(tag['type']));
        this.logger.info(methodName + 'tag: ' + JSON.stringify(tag));
        this.prepareScannedData(tag);
    },
    (err) => {
      console.log('Error reading tag', err);
      this.nfcReaderMode.unsubscribe();
      this.setPreventNFCListener();
    });
  }


  private async showScanReadyMessageAndroid() {
    const dataPackage: any = {
      type: 'scanNfcReadyNotice'
    };

    this.nfcReadyPopover = await this.popoverCtrl.create({
      component: PopoverComponent,
      cssClass: 'scan-nfc-ready',
      backdropDismiss: false,
      componentProps: {dataPackage}
    });

    await this.nfcReadyPopover.present();
    await this.nfcReadyPopover.onDidDismiss()
    .then(popoverData => {
      if (popoverData.data === 'cancel') {
        this.nfcReaderMode.unsubscribe();
        this.setPreventNFCListener();
        this.scanning.set(false);
      }
    });
  }

  private prepareScannedData(event) {
    const methodName = 'prepareScannedData()';
    this.logger.info(methodName);
    this.vibration.vibrate(500);

    if (this.isAndroid) {
      this.nfcReadyPopover.dismiss();
      this.nfcReaderMode.unsubscribe();
      this.setPreventNFCListener();
    }

    this.scannedNfcId = this.getTagId(event);
    this.scanningMethod = SECURE_PRINT_LEVELS.NFC;
    this.scanning.set(true);

    this.findScannedPrinter();
  }

  private getTagId(event) {
    const methodName = 'getTagId() ';
    this.logger.info(methodName);

    let scannedTagId = null;

    const tagId = event['id'];
    this.logger.info(methodName + 'tagId: ' + tagId);

    if (tagId) {
      scannedTagId = this.nfc.bytesToHexString(tagId);
      this.logger.info(methodName + '#### scannedTagId: ' + scannedTagId);
    } else {
      this.dialogService.showAlertDialog('Tag does not have an id, and cannot have been registered with a printer').subscribe();
    }
    return scannedTagId;
}

  // ##################### /NFC SCANNING ################################################################################

  // ######################## QR SCANNING ##############################################################################################################

  public openQRScanner = function (): void { // VIEW
    const methodName = 'openQRScanner() ';
    this.logger.info(methodName);

    this.broadcaster.broadcast('DISABLE_RESUME');

    this.qrScanner.prepare().then((status: QRScannerStatus) => {
      this.broadcaster.broadcast('ENABLE_RESUME');
      if (status.authorized) {
        console.log('this.selectedPrintJobs().length', this.selectedPrintJobs().length);
        this.toggleQRScanningState(true);
      } else if (status.denied) {
        // Camera permission permanently denied — guide the user to Settings via QRScanner.openSettings() so they can grant it there.
        this.logger.info(methodName + 'status.denied, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes')
        .subscribe(() => {
          this.qrScanner.openSettings();
        });
      } else {
        this.logger.info(methodName + 'status not authorized, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes')
        .subscribe(() => {
          this.qrScanner.openSettings();
        });
        // permission was denied, but not permanently. You can ask for permission again at a later time.
      }
    })
    .catch((e: any) => this.logger.info(methodName + 'qrScanner.prepare() ERROR: ' + JSON.stringify(e)));

  };

  private toggleQRScanningState(on: boolean): void {
    this.logger.info('toggleQRScanningState()');
    this.scanning.set(on);
    this.showTransparentBody.set(on);
    if (on) {
      this.scanQRCode();
    } else {
      this.qrScanner.hide();
      this.qrScanner.destroy();
    }
  }

  public scanQRCode(): void { // openQRScanner()
    const methodName = 'scanQRCode() ';
    this.logger.info(methodName);
    this.qrScanner.show().then(() => {
      this.qrScanner.scan()
      .pipe(takeUntil(this.unSubscribeQRScanner))
      .subscribe((qrUrl: string) => {
        this.logger.info(methodName + 'qrScanner.scan()');
        this.logger.info(methodName + 'scannedQRUrl: ' + qrUrl);
        this.scannedQRUrl = qrUrl;
        this.vibration.vibrate(500);
        this.unSubscribeQRScanner.next();
        this.unSubscribeQRScanner.complete();
        if (this.checkHostingDomain()) {
          this.scanningMethod = SECURE_PRINT_LEVELS.QR;
          this.findScannedPrinter();
        } else {
          this.invalidQRCode.set(true);
          setTimeout(() => {
            this.invalidQRCode.set(false);
            this.scanQRCode();
          }, 4000);
        }
      }, (error) => {
        console.log('error', error);
      });
    });
  }

  // ##################### /QR SCANNING #######################################################################################

  // ##################### FIND PRINTER #######################################################################################

  private findScannedPrinter(): void {
    this.logger.info('findScannedPrinter()');
    switch (this.scanningMethod) {
      case 'QR_SCAN':
        this.getPrinterFromSignId();
      break;
      case 'NFC_SCAN':
        this.getPrinterFromTagId();
      break;
    }
  }

  private getPrinterFromSignId(): void { // QR scan
    this.logger.info('QR-scan getPrinterFromSignId()');
    const urlSplit: any = this.scannedQRUrl.split('/');
    const signIdIndex: number = urlSplit.length - 1;
    this.scannedSignId = urlSplit[signIdIndex];
    const printerParameters: any = this.pagingService.getPrinterParameters();
    printerParameters.signIds = [];
    printerParameters.signIds.push(this.scannedSignId);
    this.dialogService.showLoadingSpinnerDialog('getPrinterFromSignId()').subscribe(() => {
      this.printerService.getPrinterList(printerParameters, this.printerUrlString)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((scannedPrinter: Array<IPrinter>) => {
        this.logsService.logScannedPrinter(scannedPrinter[0], this.printJobsPageType);
        this.selectedPrinter = scannedPrinter[0];
        this.selectedPrinterUrl.set(scannedPrinter[0].links.self);
        this.toggleQRScanningState(false);
        setTimeout(() => {
          this.dialogService.hideLoadingSpinnerDialog('getPrinterFromSignId()');
          this.checkForSelectedPrintJobs();
        }, 50);
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - getPrinterFromSignId()');
        this.logger.info('QR-scan getPrinterFromSignId() SCANNED PRINTER NOT FOUND');
        this.scanning.set(false);
        this.concludeScan();
        this.dialogService.showAlertDialog('Scanned Printer not found').subscribe();
      });
    });
  }

  private checkHostingDomain(): boolean {
    this.logger.info('checkHostingDomain()');
    const urlSplit: string[] = this.scannedQRUrl.split('/');
    let validHostingDomain = false;
    let hostingDomain: string = null;

    const isValidHostingDomain = (url: string, domain: string): boolean => {
      return url.includes(domain) && url === this.tenant().tenantData.hostingDomain;
    }

    const domains = ['.printix.net', '.printix.dev']; // valid domains for QR code

    for (const url of urlSplit) {
      if (domains.some(domain => isValidHostingDomain(url, domain))) {
        hostingDomain = url;
        validHostingDomain = true;
        break;  // exit the loop early if a valid domain is found
      }
    }

    return validHostingDomain;
  }

  private getPrinterFromTagId() {
    this.logger.info('getPrinterFromTagId()');
    this.dialogService.showLoadingSpinnerDialog('getPrinterFromTagId()').subscribe(() => {
      this.nfcService.getPrinterFromTagId(this.scannedNfcId)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((scannedPrinter: IPrinter) => {
        this.dialogService.hideLoadingSpinnerDialog('getPrinterFromTagId()');
        this.logsService.logScannedPrinter(scannedPrinter, this.printJobsPageType);
        this.broadcaster.broadcast('ENABLE_RESUME');
        this.selectedPrinter = scannedPrinter;
        this.selectedPrinterUrl.set(scannedPrinter.links.self);
        this.checkForSelectedPrintJobs();
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - getPrinterFromTagId()');
        this.scanning.set(false);
        this.concludeScan();
        if (error.status === 404) {
          this.logger.info('getPrinterFromTagId() - ## TAG_NOT_FOUND ##');
          console.log('## TAG_NOT_FOUND ##');
          this.dialogService.showTranslatedMessageDialog('TagNotFound');
        } else {
          this.errorService.handleHttpClientResponseError(error, 'GET', 'getPrinterFromTagId()')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        }
      });
    });
  }

  // ##################### /FIND PRINTER ######################################################################################

  // ##################### CHECK FOR SELECTED PRINT JOBS ###################################################################################

  private checkForSelectedPrintJobs(): void {
    this.logger.info('checkForSelectedPrintJobs()');
    const arePrintJobsSelected = this.selectedPrintJobs().length > 0 ? true : false;
    if (arePrintJobsSelected) {
      if (this.hasSelectedAnywhereJobs()) {
        this.handleOnlyAnywhereJobsSelected();
      } else {
        this.handleOnlyLaterJobsSelected();
      }
    } else {
      let hasAnywhereJobs = false;

      this.printerList().forEach(printer => {
        if (printer.type === 'FREEDOM') {
          hasAnywhereJobs = true;
        }
      });

      this.autoSelectPrintJobsForScannedRelease(hasAnywhereJobs);
    }
  }

  private hasSelectedAnywhereJobs(): boolean {
    let hasSelectedAnywhereJobs: boolean = false;

    this.selectedPrintJobs().forEach(printJob => {
      this.printerList().forEach(printer => {
        if (printJob.links.printer === printer.links.self && printer.type === 'FREEDOM') {
          hasSelectedAnywhereJobs = true;
        }
      });
    });
    this.logger.info('hasSelectedAnywhereJobs() hasSelectedAnywhereJobs: ' + hasSelectedAnywhereJobs);
    return hasSelectedAnywhereJobs;
  }

    // ##################### SELECTED ANYWHERE JOBS ONLY ##################################################################################
      private handleOnlyAnywhereJobsSelected() {
        this.checkQueuesForRelease();
      }
    // ##################### /SELECTED ANYWHERE JOBS ######################################################################################


    // ##################### SELECTED LATER JOBS ONLY #####################################################################################
      private handleOnlyLaterJobsSelected() {
        if (this.matchingPrinter()) {
          if (this.selectedPrinter.queues.length !== 0) { // The queue, in this case is not important, since it is only later documents, and the the server will set the correct queue, anyway.
            this.selectedQueue = this.selectedPrinter.queues[0];
            this.updateReleaseResourceBeforeRelease(this.selectedPrinter, this.selectedPrintJobs(), this.selectedQueue); // ONLY LATER JOBS
          } else {
            this.dialogService.hideLoadingSpinnerDialog('handleOnlyLaterJobsSelected() - NoPrintQueue');
            this.scanning.set(false);
            this.concludeScan();
            this.dialogService.showTranslatedMessageDialog("NoPrintQueue");
          }
        } else {
          this.dialogService.hideLoadingSpinnerDialog('unMatchedPrinter - handleOnlyLaterJobsSelected()');
          if (this.appIsActive()) {
           this.presentAlert(this.selectedPrinter, this.selectedPrintJobs(), 'unMatchedPrinter');
          }
        }
      }

      private matchingPrinter() {
        // is the print job printer and the scanned printer matching up
        return this.selectedPrinter.links.self === this.selectedPrintJobs()[0].links.printer;
      }
    // ##################### /SELECTED LATER JOBS ####################################################################################

    private autoSelectPrintJobsForScannedRelease(hasAnywhereJobs) {
      this.logger.info('autoSelectPrinJobsForScannedRelease()');
      const printersWithEligiblePrintJobs: Array<IPrinter> = [];

      this.printerList().forEach(printer => { // collect anywhere printer as well as later printer matching the scanned printer id
        if (printer.signId === this.selectedPrinter.signId) {
          printersWithEligiblePrintJobs.push(printer);
        } else if (printer.type === 'FREEDOM') {
          printersWithEligiblePrintJobs.push(printer);
        }
      });

      this.printJobList().forEach(printJob => { // find print jobs on the eligible printers
        printersWithEligiblePrintJobs.forEach(printer => {
          if (printJob.links.printer === printer.links.self) {
            this.selectedPrintJobs.update(jobs => [...jobs, printJob]);
          }
        });
      });

      if (this.selectedPrintJobs().length > 0) {
        this.handleSelectedPrintJobs(hasAnywhereJobs);
      } else {
         this.dialogService.hideLoadingSpinnerDialog('autoSelectPrintJobsForScannedRelease() - unMatchedPrinter');
        if (this.appIsActive()) {
          this.presentAlert(this.selectedPrinter, this.selectedPrintJobs(), 'unMatchedPrinter');
        }
      }
    }

    private handleSelectedPrintJobs(hasAnywhereJobs) {
      if (this.selectedPrinter.queues.length !== 0) { // The queue, in this case is not important, since it is only later documents, and the the server will set the correct queue, anyway.
        if (hasAnywhereJobs) {
          this.checkQueuesForRelease();
        } else {
          this.selectedQueue = this.selectedPrinter.queues[0];
          this.updateReleaseResourceBeforeRelease(this.selectedPrinter, this.selectedPrintJobs(), this.selectedQueue);  // ONLY LATER JOBS
        }
      } else {
        this.dialogService.hideLoadingSpinnerDialog('handleSelectedPrintJobs() - NoPrintQueue');
        this.scanning.set(false);
        this.concludeScan();
        this.dialogService.showTranslatedMessageDialog("NoPrintQueue");
      }
    }

  // ##################### /CHECK FOR SELECTED PRINT JOBS #################################################################################


  // ##################### CHECK QUEUES FOR RELEASE #################################################################################

  private checkQueuesForRelease(): void { // only for Anywhere jobs
    const methodName = 'checkQueuesForRelease() ';
    this.logger.info(methodName);
    const releaseQueuesUrl: string = this.user()['links']['releaseQueues'];

    this.printJobsToAdd = []; // reset print jobs to add before setting them in order to avoid duplicates
    this.selectedPrintJobs().forEach(printJob => { // add all selected print job id's to addPrintJobs array
      this.printerList().forEach(printer => {
        if (printer.type === 'FREEDOM' && printJob.links.printer === printer.links.self) {
          this.printJobsToAdd.push(printJob.links.self);
        }
      });
    });

    this.releaseResourceService.getAvailableQueuesForRelease(releaseQueuesUrl, this.printJobsToAdd, this.selectedPrinter.links.self)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((availableQueues) => {
        this.logger.info(methodName + 'Available queues from server:');
        this.logsService.logAvailableQueues(availableQueues, this.printJobsPageType);
        if (availableQueues.length > 0) {

          this.setAvailableQueuesForPrinter(availableQueues);

          if (this.selectedPrinter.queues.length === 1) {
            this.selectedQueue = this.selectedPrinter.queues[0];
            this.updateReleaseResourceBeforeRelease(this.selectedPrinter, this.selectedPrintJobs(), this.selectedQueue); // THE ONE AND ONLY QUEUE
          } else {
            this.dialogService.hideLoadingSpinnerDialog('checkQueuesForRelease() - getAvailableQueuesForRelease - selectPrintQueue');
            this.presentModal(null, this.selectedPrinter, 'selectPrintQueue', false, this.selectedPrintJobs());
          }
        } else {
          this.dialogService.hideLoadingSpinnerDialog('checkQueuesForRelease() - getAvailableQueuesForRelease - No available resources');
          this.logger.info(methodName + '## No available resources ##');
          this.selectedPrintJobs.set([]);
          this.selectedQueue = null;
          this.scanning.set(false);
          this.concludeScan();
          this.dialogService.showTranslatedMessageDialog('Printer not found');
        }
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('checkQueuesForRelease() - ERROR - getAvailableQueuesForRelease');
        this.handleHttpErrorOnScan(error, this.selectedPrinter.queues[0]); // UPDATE SCANNED PRINTER QUEUE
      });
  }

  private setAvailableQueuesForPrinter(availableQueues): void {
    const methodName = 'setAvailableQueuesForPrinter() ';
    this.logger.info(methodName);

    const queuesToSelect: Array<IQueue> = [];

    availableQueues.forEach(q => { // Get the favorite queues
      if (q.active && this.favoriteQueueUrls.indexOf(q.links.self) > -1) {
        q.isFavorite = true;
        queuesToSelect.push(q);
      }
    });

    if (queuesToSelect.length !== 1) { // if there is more than one favorite queue, to select, give a choice of other active queues to select
      availableQueues.forEach(q => {
        if (q.active && queuesToSelect.indexOf(q) === -1) {
          queuesToSelect.push(q);
        }
      });
    }

    this.logger.info(methodName + 'Available queues active on printer');
    this.logsService.logAvailableQueues(queuesToSelect, this.printJobsPageType);
    this.selectedPrinter.queues = queuesToSelect;
  }

  // ##################### /CHECK QUEUES FOR RELEASE ###########################################################################


  // ##################### RELEASE ON SCANNING ################################################################################

  // ######################### UPDATE RESOURCE ############################# //
  private updateReleaseResourceBeforeRelease(printer: IPrinter, printJobs: Array<IPrintJob>, queue: IQueue): void {
    this.logger.info('updateReleaseResourceBeforeRelease()');
    this.printJobsToAdd = [];
    const queueUrl: string = queue ? queue.links.self : null;
    // Derived above the guard because the create below needs it too: without it createRelease()
    // falls back to this.selectedPrinter, which the scanned queue may not belong to.
    const printerUrl: string = printer ? printer.links.self : null;
    if (!this.releaseResource) {
      this.logger.info('updateReleaseResourceBeforeRelease() - releaseResource is null, aborting release');
      this.scanning.set(false);
      this.concludeScan();
      this.dialogService.hideLoadingSpinnerDialog('updateReleaseResourceBeforeRelease() - releaseResource is null');
      this.dialogService.showTranslatedMessageDialog('Something went wrong, please try to release again');
      this.createRelease(queueUrl, false, printerUrl);
      return;
    }
    const resourceId: string = this.releaseResource.resourceId;

    printJobs.forEach(printJob => {
      this.printJobsToAdd.push(printJob.links.self);
    });
    let releaseHandedOff = false;
    this.releaseResourceService.updatePrintJobsOnRelease(this.printJobsToAdd, printerUrl, queueUrl, resourceId, this.scanningMethod)
    .pipe(takeUntil(this.cancelPendingReleaseUpdate), takeUntil(this.unSubscribe),
          finalize(() => {
            if (!releaseHandedOff && this.scanning()) {
              this.scanning.set(false);
              this.concludeScan();
              this.dialogService.hideLoadingSpinnerDialog('updateReleaseResourceBeforeRelease() - torn down');
            }
          }))
    .subscribe((response) => {
      this.logsService.logUpdatedReleaseItemsBeforeReleaseStart(response, this.printJobsPageType);
      this.releaseResource = response;
      const releaseJobs: Array<any> = response.releaseJobs;

      this.unavailablePrintJobs.set(false);
      this.setPrinterAccessError(response.printerQueue);
      this.setReleaseJobsErrors(releaseJobs);

      this.printJobsToAdd = [];
      // Hands off to another PATCH that still needs this.scanningMethod — concludeScan() runs once that terminates.
      if (this.scanningMethod === 'QR_SCAN') {
        this.cancelQRScanner();
      }
      this.dialogService.hideLoadingSpinnerDialog('updateReleaseResourceBeforeRelease()');
      releaseHandedOff = true;
      if (this.unavailablePrintJobs()) {
        this.presentAlert(this.selectedPrinter, releaseJobs, 'releaseMatchingPrintJobs');
      } else {
        if (queue && queue.isFavorite !== true) {
          queue.isFavorite = true;
        }
        queue = this.hasSelectedAnywhereJobs() ? queue : null;
        this.releaseSelectedPrintJobs(printer, printJobs, queue);
      }
    }, (error) => {
       this.dialogService.hideLoadingSpinnerDialog('ERROR - updateReleaseResourceBeforeRelease()');
      this.handleHttpErrorOnScan(error, queue);
    });
  }

  private handleHttpErrorOnScan(error, queue?: IQueue): void {
    this.logger.info('handleHttpErrorOnScan()');
    if (error.status === 400 && error.error.errorCode === 'PATCHING_FINISHED_RELEASE') {
      this.logger.info('ReleaseResource expired');
      this.dialogService.showTranslatedMessageDialog("Something went wrong, please try to release again");
    }
    this.selectedPrintJobs.set([]);
    this.selectedQueue = null;
    this.scanning.set(false);
    this.concludeScan();
    this.errorService.handleHttpClientResponseError(error, 'PATCH', '[ReleaseResourceService] updatePrintJobsOnRelease()')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        } else {
          if (this.appIsActive()) {
            console.log('## refreshPrintJobsAndPrinters() - handleHttpErrorOnScan() - no selected queue()');
            this.refreshPrintJobsAndPrinters().subscribe();
          }
        }
      });
  }

  // Ends the scan: tears down an open QR scanner, then drops the QR/NFC tag so it cannot outlive the scan it belongs to.
  private concludeScan(): void {
    const methodName = 'concludeScan() ';
    this.logger.info(methodName + JSON.stringify({ scanningMethod: this.scanningMethod }));
    if (this.scanningMethod === 'QR_SCAN') {
      this.cancelQRScanner();
    }
    this.scanningMethod = null;
  }

  public cancelQRScanner = function (): void {
    this.logger.info('cancelQRScanner()');
    this.invalidQRCode.set(false);
    if (this.unSubscribeQRScanner) {
      this.unSubscribeQRScanner.complete();
    }
    this.toggleQRScanningState(false);

    this.printerList().forEach(printer => { // uncheck 'isAllSelected' button when all printJobs are cancelled from the qr-scan view
      if (printer.type === 'FREEDOM') {
        this.isAllSelected(printer);
      }
    });
  };

  // ##################### /RELEASE ON SCANNING ################################################################################

  //################### LISTENERS #################################################################

  private startListener(): void {
    const methodName = 'startListener() ';
    this.logger.info(methodName);

    this.jobAddedSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.JOB_ADDED) // get request
    .subscribe(data => {
      console.log('**PUSHER job added', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER job added');
      if (!this.user()) {
        this.logger.info('job_added skipped: user not loaded');
        return;
      }
      if (this.shouldProcessEvents()) {
        this.refreshPrintJobsAndPrinters().pipe(takeUntil(this.unSubscribe)).subscribe();
      }
    });

    this.jobRemovedSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.JOB_REMOVED)
    .subscribe(data => {
      console.log('**PUSHER job removed', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER job removed');
      if (this.printJobsPageType !== 'PrintJobsPage' && !data['permanent']) {
        // Do nothing here — printing also fires a 'job removed' Pusher event on the recent page; only remove jobs there when the user deletes them.
      } else {
        this.pendingDeletedJobUrls.add(data['job']);
        if (this.shouldProcessEvents()) {
          this.handleRemovedJobEvent(data);
        } else {
          const methodName = 'job_removed ';
          this.logger.info(methodName + 'dropped, shouldProcessEvents=false ' +
            JSON.stringify({ scanning: this.scanning(), appIsActive: this.appIsActive(), job: (data as any)['job'] }));
        }
      }
    });

    this.jobUpdatedSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.JOB_UPDATED) // TEST THIS ********
    .subscribe((data: any) => {
      console.log('**PUSHER job updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER job updated');
      if (this.shouldProcessEvents()) {
        this.handleJobUpdatedEvent(data);
      } else {
        const methodName = 'job_updated ';
        this.logger.info(methodName + 'dropped, shouldProcessEvents=false ' +
          JSON.stringify({ scanning: this.scanning(), appIsActive: this.appIsActive(), job: data['job'], jobState: data['jobState'] }));
      }
    });

    if (!this.createdReleaseSubscription) {
      this.createdReleaseSubscription = this.broadcaster.on<string>(CHANNELS.SPECIFIC_USER.EVENTS.USER_RELEASE_CREATED)
      .subscribe((data) => {
          // check state to see if page needs refresh
          console.log('**PUSHER release CREATED', 'printJobList', data);
          this.logger.info('**PUSHER release CREATED');
      });
    }

    this.releaseUpdatedSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.USER_RELEASE_UPDATED) // TEST THIS ********
    .subscribe((data: any) => {
      console.log('**PUSHER release updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER release updated');
      if (!this.user()) {
        this.logger.info('release_updated skipped: user not loaded');
        return;
      }
      const stateAfter: string = data['releaseStateChange'] ? data['releaseStateChange']['stateAfter'] : null;
      // Deliberately ahead of shouldProcessEvents(): the abort arrives ~an hour after the release's
      // last change, exactly when the page is idle, scanning or backgrounded — the states that
      // guard filters out. Dropping a dead reference issues no request and touches no UI, and it is
      // the one event that must not be missed.
      if (stateAfter === 'ABORTED') {
        this.discardAbortedReleaseResource(data['release']);
        return;
      }
      // Unguarded for the same reason, so the diagnostic is not hidden when it is most needed.
      // CREATING and RELEASED are excluded because every normal release passes through them and
      // would bury what this line is for: TIMEOUT, FAILED and anything not yet seen.
      if (stateAfter && stateAfter !== 'FINISHED' && stateAfter !== 'CREATING' && stateAfter !== 'RELEASED') {
        this.logger.info('release_updated unhandled state - ' + JSON.stringify({ stateAfter: stateAfter }));
      }
      if (this.shouldProcessEvents() && stateAfter === 'FINISHED') {
        this.refreshPrintJobsAndPrinters().subscribe();
      }
    });

    this.printerStatusSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINTER_STATUS_UPDATE)
    .subscribe(data => {
      console.log('**PUSHER printer status updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER printer status updated');
      if (this.shouldProcessEvents()) {
        this.handlePrinterStatusUpdatedEvent(data);
      }
    });

    this.printerRemovedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINTER_REMOVED) // TEST THIS *******
    .subscribe(data => {
      console.log('**PUSHER printer removed', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER printer removed');
      if (!this.user()) {
        this.logger.info('printer_removed skipped: user not loaded');
        return;
      }
      if (this.shouldProcessEvents()) {
        console.log('## refreshPrintJobsAndPrinters() - PUSHER printer removed');
        this.refreshPrintJobsAndPrinters().subscribe();
      }
    });

    this.printerUpdatedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINTER_UPDATE)
    .subscribe(data => {
      console.log('**PUSHER printer updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER printer updated');
      if (this.shouldProcessEvents()) {
        this.handlePrinterUpdatedEvent(data);
      }
    });

    this.printQueueUpdatedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINT_QUEUE_UPDATED)
    .subscribe(data => {
      console.log('**PUSHER print queue updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER print queue updated');
      if (this.shouldProcessEvents()) {
        this.handlePrintQueueEvents(data, false);
      }
    });

    this.printQueueGroupsUpdatedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINT_QUEUE_GROUPS_UPDATED)
    .subscribe(data => {
      console.log('**PUSHER print queue groups updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER print queue groups updated');
      if (this.shouldProcessEvents()) {
        this.handlePrintQueueEvents(data, false);
      }
    });

    this.printQueueRemovedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.PRINT_QUEUE_REMOVED) // remove queue
    .subscribe(data => {
      console.log('**PUSHER print queue removed', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER print queue removed');
      if (!this.user()) {
        this.logger.info('print_queue_removed skipped: user not loaded');
        return;
      }
      if (this.shouldProcessEvents()) {
        console.log('## refreshPrintJobsAndPrinters() - PUSHER print queue removed');
        this.refreshPrintJobsAndPrinters().subscribe();
      }
    });

    this.tenantUpdatedEventSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.TENANT_UPDATED)
    .subscribe(data => {
      console.log('**PUSHER tenant updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER tenant updated');
      if (!this.scanning()) {
        this.tenantService.refreshStoredTenant()
        .subscribe(() => {
          console.log('## PJ_PAGE NEW_REFRESHED_TENANT');
        }, (error) => {
          this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        });
      }
    });

    if (!this.updateSecurePrintSubscription) {
      this.updateSecurePrintSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.SECURE_PRINT_CONFIGURATION_UPDATED)
      .subscribe(data => {
        console.log('**PUSHER secure print configuration updated', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
        this.logger.info('**PUSHER secure print configuration updated');
        console.log('## SECURE_PRINT_CONFIG_UPDATED() - ', this.scanning());
        console.log('## loadPrintPage() - SECURE_PRINT_CONFIG_UPDATED()', this.securePrintLevel());
        if (!this.scanning()) {
          this.loadPrintPage();
        }
      });
    }

    this.batchExecutedSubscription = this.broadcaster.on<object>(CHANNELS.ALL_TENANT_USERS.EVENTS.BATCH_EXECUTED)
    .subscribe(data => {
      console.log('**PUSHER BATCH EXECUTED', this.printJobsPageType === 'PrintJobsPage' ? 'printJob page' : 'recent page', data);
      this.logger.info('**PUSHER BATCH EXECUTED');
      if (!this.user()) {
        this.logger.info('batch_executed skipped: user not loaded');
        return;
      }
      if (this.shouldProcessEvents()) {
        console.log('## refreshPrintJobsAndPrinters() - PUSHER BATCH EXECUTED');
        this.refreshPrintJobsAndPrinters().subscribe();
      }
      if (data['reference'] === 'PRINT_QUEUES_DELETE_BATCH') {
      }
    });

    this.platformPauseEventSubscription = this.platform.pause.subscribe(async () => {
      this.logger.info('platform.pause fired - setting appIsActive=false');
      this.appIsActive.set(false);
    });

    // platform.resume always fires on foreground; PLATFORM_RESUMED is EventService's opt-in broadcast. Both reconcile the list because backgrounded Pusher events were dropped (gated by shouldProcessEvents()), not queued.
    this.platformResumeEventSubscription = this.platform.resume.subscribe(async () => {
      this.reloadOnResume('platform.resume');
    });

    this.platformResumeBroadcastEventSubscription = this.broadcaster.on('PLATFORM_RESUMED').subscribe(() => {
      this.logger.info('PLATFORM_RESUMED');
      this.reloadOnResume('PLATFORM_RESUMED');
    });

    this.reloadPageEventSubscription = this.broadcaster.on('reload_page').subscribe(() => {
      this.logger.info(methodName + 'reload_page');
      if (this.shouldProcessEvents()) {
        this.loadPrintPage(); // for native
      }
    });

    this.languageChangeSubscription = this.broadcaster.on<string>('languageChange')
    .subscribe(data => {
      this.currentLanguage = this.localeService.formatLocaleString(data);
    });
  }

  // Reconcile the print-job list on foreground resume: backgrounded events were dropped (shouldProcessEvents()), so a full refetch resyncs; called from both resume paths, de-duplicated to refetch once.
  private reloadOnResume(source: string): void {
    const methodName = 'reloadOnResume()';
    this.appIsActive.set(true);
    if (this.scanning() || !this.isOnline()) {
      this.logger.info(methodName + ' skipped (scanning or offline) - source: ' + source);
      return;
    }
    const now = Date.now();
    if (now - this.lastResumeReloadAt < this.RESUME_RELOAD_DEDUP_MS) {
      this.logger.info(methodName + ' skipped (recent reload) - source: ' + source);
      return;
    }
    this.lastResumeReloadAt = now;
    this.logger.info(methodName + ' reloading print jobs - source: ' + source);
    this.loadPrintPage();
  }

// ################### HANDLE LISTENERS #################################################################


  private handleRemovedJobEvent(data: object): void { // will happen both, when printJobs are deleted, and when they are printed
    const methodName = 'handleRemovedJobEvent() ';
    const jobUrl: string = data['job'];

    if (!this.printJobList().some(pj => pj.links.self === jobUrl)
        && !this.selectedPrintJobs().some(pj => pj.links.self === jobUrl)) {
      this.logger.info(methodName + 'job not found in printJobList/selectedPrintJobs, no-op ' + JSON.stringify({ job: jobUrl }));
      return;
    }

    this.logger.info(methodName + 'removing job ' + JSON.stringify({ job: jobUrl }));
    this.removeJobsFromLocalState([jobUrl]);
  }

  /** True when a newer fetch has already been applied, making `seq` stale. */
  private isStaleFetch(seq: number): boolean {
    return seq < this.appliedFetchSeq;
  }

  /** Apply server job list, filtering by page type and tombstone guard. Returns false if stale. */
  private applyServerJobList(printJobList: Array<IPrintJob>, seq: number): boolean {
    const methodName = 'applyServerJobList()';

    if (this.isStaleFetch(seq)) {
      this.logger.info(methodName + ` - dropped stale response (seq=${seq}, applied=${this.appliedFetchSeq})`);
      return false;
    }
    this.appliedFetchSeq = seq;

    let filteredJobs = this.printJobsPageType === 'PrintJobsPage'
      ? printJobList.filter(printJob => !printJob.deletedTime)
      : printJobList.filter(printJob => printJob.deletedTime);

    if (this.pendingDeletedJobUrls.size > 0) {
      const pendingBefore = this.pendingDeletedJobUrls.size;
      const serverJobUrls = new Set(filteredJobs.map(j => j.links.self));
      for (const url of this.pendingDeletedJobUrls) {
        if (!serverJobUrls.has(url)) {
          this.pendingDeletedJobUrls.delete(url);
        }
      }
      const cleared = pendingBefore - this.pendingDeletedJobUrls.size;
      if (cleared > 0) {
        this.logger.info(methodName + ` - cleared ${cleared} pending removal(s) (server confirmed)`);
      }
      const suppressed = filteredJobs.filter(j => this.pendingDeletedJobUrls.has(j.links.self)).map(j => j.links.self);
      filteredJobs = filteredJobs.filter(job => !this.pendingDeletedJobUrls.has(job.links.self));
      this.printJobList.set(filteredJobs);
      if (suppressed.length > 0) {
        this.removeJobsFromLocalState(suppressed);
      }
    } else {
      this.printJobList.set(filteredJobs);
    }

    this.logger.info(methodName + ` - print jobs loaded: ${filteredJobs.length} (seq=${seq})`);

    return true;
  }

  /** Remove jobs from printJobList/selectedPrintJobs and drop orphan printers. */
  private removeJobsFromLocalState(jobUrls: Array<string>): void {
    const methodName = 'removeJobsFromLocalState()';
    this.logger.info(methodName + ` - removing ${jobUrls.length} job(s)`);
    const urlSet = new Set(jobUrls);

    const affectedPrinterUrls = new Set<string>();
    this.printJobList().forEach(job => {
      if (urlSet.has(job.links.self)) {
        affectedPrinterUrls.add(job.links.printer);
      }
    });

    this.printJobList.update(jobs => jobs.filter(job => !urlSet.has(job.links.self)));

    const selectedBefore = this.selectedPrintJobs().length;
    this.selectedPrintJobs.update(jobs => jobs.filter(job => !urlSet.has(job.links.self)));

    if (this.selectedPrintJobs().length < selectedBefore) {
      this.logger.info(methodName + ' - selection shrank, invalidating releaseResource');
      this.releaseResource = null;
      this.cancelPendingReleaseUpdate.next();
      // Re-derive allowToPrint so printers reflect the new selection size.
      this.resetPrinterAllowToPrintProperty(
        this.selectedPrintJobs().length,
        this.selectedPrintJobs().length > 0 ? this.selectedPrintJobs()[0] : null);
    }

    const printersWithRemainingJobs = new Set(this.printJobList().map(job => job.links.printer));
    const printersToRemove = new Set<string>();
    affectedPrinterUrls.forEach(url => {
      if (!printersWithRemainingJobs.has(url)) {
        printersToRemove.add(url);
      }
    });
    if (printersToRemove.size > 0) {
      this.printerList.update(printers => printers.filter(p => !printersToRemove.has(p.links.self)));
    }
  }

  private handleJobUpdatedEvent(data: object): void {
    const methodName = 'handleJobUpdatedEvent() ';
    this.logger.info(methodName);
    const jobStates: Array<string> = this.printJobService.PRINT_JOB_APP_STATES;
    let jobIndex: number = null;

    this.printJobList().forEach((printjob, index) => {
      if (printjob.links.self === data['job']) {
        jobIndex = index;
      }
    });
    if (jobIndex !== null) {
     this.handleUpdatedPrintJobs(jobStates, data, jobIndex);
    } else {
      this.logger.info(methodName + 'job not found in printJobList, update dropped ' +
        JSON.stringify({ job: (data as any)['job'], jobState: (data as any)['jobState'] }));
    }
  }

  private handleUpdatedPrintJobs(jobStates, data, jobIndex: number) {
    this.logger.info('handleUpdatedPrintJobs()');
    
    // Calculate ongoing release
    let ongoingRelease: Array<any> = [];
    if (data['_links'] && data['_links']['px:ongoingRelease'] && data['_links']['px:ongoingRelease']['href']) {
      ongoingRelease.push(data['_links']['px:ongoingRelease']);
    } else if (data['_links'] && data['_links']['px:ongoingRelease'] && isArray(data['_links']['px:ongoingRelease'])) {
      ongoingRelease = data['_links']['px:ongoingRelease'];
    }

    // Update the print job list signal immutably
    this.printJobList.update(jobs => {
      const newJobs = [...jobs];
      const currentJob = newJobs[jobIndex];
      
      // Check if jobState matches any valid state
      const isValidState = jobStates.includes(data['jobState']);
      
      if (isValidState) {
        const matchedStatus = PRINT_JOB_STATUS.find(s => s.status === data['jobState']);
        const printJobStatus = matchedStatus ?? {
          status: data['jobState'],
          showStatusInfo: false,
          showLoader: false,
          isSuccess: false,
          isError: false,
        };
        newJobs[jobIndex] = {
          ...currentJob,
          jobState: data['jobState'],
          options: {
            ...currentJob.options,
            printJobStatus
          },
          links: {
            ...currentJob.links,
            ongoingRelease: ongoingRelease
          }
        };
        
        // Handle CONVERTED state to update page count
        if (data['jobState'] === 'CONVERTED') {
          this.printJobService.getSinglePrintJob(data['job'])
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((printJob: IPrintJob) => {
            this.printJobList.update(jobs => {
              const updatedJobs = [...jobs];

              // Job position may change after batched events; update by id, not stale index.
              const updatedJobIndex = updatedJobs.findIndex(job => job.links.self === data['job']);
              if (updatedJobIndex === -1) {
                return updatedJobs;
              }

              updatedJobs[updatedJobIndex] = {
                ...updatedJobs[updatedJobIndex],
                pageCount: printJob['pageCount']
              };
              return updatedJobs;
            });
          });
        }
      } else {
        // Just update ongoing release if state doesn't match
        newJobs[jobIndex] = {
          ...currentJob,
          links: {
            ...currentJob.links,
            ongoingRelease: ongoingRelease
          }
        };
      }
      
      return newJobs;
    });
    
    this.changeDetectorRef.detectChanges();
  }

  private handlePrinterStatusUpdatedEvent(data: object): void {
    this.logger.info('handlePrinterStatusUpdatedEvent()');
    const updatedPrinterUrl = data['printer'];
    
    // Update printer in printerList if found
    this.printerList.update(printers => {
      return printers.map(printer => {
        if (printer.links.self === updatedPrinterUrl) {
          return {
            ...printer,
            embedded: {
              ...printer.embedded,
              status: {
                ...printer.embedded.status,
                warnings: data['warnings'] || [],
                errors: data['errors'] || []
              }
            }
          };
        }
        return printer;
      });
    });
    
    // Also update selectedPrinter if it matches (may not be in printerList)
    if (this.selectedPrinter && this.selectedPrinter.links.self === updatedPrinterUrl) {
      this.selectedPrinter = {
        ...this.selectedPrinter,
        embedded: {
          ...this.selectedPrinter.embedded,
          status: {
            ...this.selectedPrinter.embedded.status,
            warnings: data['warnings'] || [],
            errors: data['errors'] || []
          }
        }
      };
    }
    
    this.changeDetectorRef.detectChanges();
  }

  private handlePrinterUpdatedEvent(data: object): void {
    this.logger.info('handlePrinterUpdatedEvent()');
    if (!data['partOfBatch']) {
      const updatedPrinterUrl = data['printer'];
      let foundInList = false;
      
      // Check if the updated printer is in printerList
      this.printerList().forEach((printer, index) => {
        if (printer.links.self === updatedPrinterUrl) {
          foundInList = true;
          this.printerService.getSinglePrinter(printer.links.self, this.printerUrlString)
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((updatedPrinter) => {
            this.printerList.update(printers => {
              const newPrinters = [...printers];
              newPrinters[index] = updatedPrinter[0];
              return newPrinters;
            });
            this.changeDetectorRef.detectChanges();
          });
        }
      });
      
      // Also check if the updated printer is the selectedPrinter (which may not be in printerList)
      if (this.selectedPrinter && this.selectedPrinter.links.self === updatedPrinterUrl) {
        this.printerService.getSinglePrinter(updatedPrinterUrl, this.printerUrlString)
        .pipe(takeUntil(this.unSubscribe))
        .subscribe((updatedPrinter) => {
          this.selectedPrinter = updatedPrinter[0];
          console.log('Updated selectedPrinter from printer updated event');
          console.log(this.selectedPrinter);
          this.changeDetectorRef.detectChanges();
        });
      }
    }
  }

  private handlePrintQueueEvents(data: object, queueRemoved: boolean): void {
    this.logger.info('handlePrintQueueEvents()');
    if (!data['partOfBatch']) {
      let printerWithQueueIndex: number = null;
      let deletedQueueIndex: number = null;
      const queueUrl = data['printQueue'];
      
      // Update queue in printerList if found
      this.printerList().forEach((printer, printerIndex) => {
        printer.queues.forEach((queue, queueIndex) => {
          if (queue.links.self === queueUrl) {
            if (!queueRemoved) {
              this.printerService.getSingleQueue(queue.links.self)
              .pipe(takeUntil(this.unSubscribe))
              .subscribe((updatedQueue) => {
                this.printerList.update(printers => {
                  const newPrinters = [...printers];
                  newPrinters[printerIndex] = {
                    ...newPrinters[printerIndex],
                    queues: newPrinters[printerIndex].queues.map((q, idx) => 
                      idx === queueIndex ? updatedQueue : q
                    )
                  };
                  return newPrinters;
                });
              }, (error) => {
                this.errorService.handleHttpClientResponseError(error, 'GET', '[PrinterService] getSingleQueue()')
                .subscribe(result => {
                  if (result === 'unauthenticated') {
                    this.logger.info('User unauthenticated');
                  }
                });
              });
            } else {
              printerWithQueueIndex = printerIndex;
              deletedQueueIndex = queueIndex;
            }
          }
        });
      });
      
      // Also update queue in selectedPrinter if it has matching queue (may not be in printerList)
      if (this.selectedPrinter && this.selectedPrinter.queues) {
        const selectedQueueIndex = this.selectedPrinter.queues.findIndex(q => q.links.self === queueUrl);
        if (selectedQueueIndex !== -1) {
          if (!queueRemoved) {
            this.printerService.getSingleQueue(queueUrl)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe((updatedQueue) => {
              this.selectedPrinter = {
                ...this.selectedPrinter,
                queues: this.selectedPrinter.queues.map((q, idx) =>
                  idx === selectedQueueIndex ? updatedQueue : q
                )
              };
            });
          } else {
            // Remove queue from selectedPrinter
            this.selectedPrinter = {
              ...this.selectedPrinter,
              queues: this.selectedPrinter.queues.filter((q, idx) => idx !== selectedQueueIndex)
            };
          }
        }
      }
      
      if (queueRemoved) {
        this.handleQueueRemoved(deletedQueueIndex, printerWithQueueIndex, data);
      }
      this.changeDetectorRef.detectChanges();
    }
  }

  private handleQueueRemoved(deletedQueueIndex, printerWithQueueIndex, data) {
    this.logger.info('handleQueueRemoved()');
    if (deletedQueueIndex !== null) {
      this.printerList.update(printers => {
        const newPrinters = [...printers];
        newPrinters[printerWithQueueIndex] = {
          ...newPrinters[printerWithQueueIndex],
          queues: newPrinters[printerWithQueueIndex].queues.filter((_, idx) => idx !== deletedQueueIndex)
        };
        return newPrinters;
      });
    }
    if (data['printQueue'] === this.user()?.userAppSettings?.lastUsedQueue) {
      this.resetPrinterAndQueue();
    }
    // remove the deleted queue from favorite queues
    let hasDeletedQueue = false;
    this.favoriteQueueUrls.forEach((qUrl, index) => {
      if (data['printQueue'] === qUrl) {
        // remove deleted q and update saved qUrls, with new array
        hasDeletedQueue = true;
        this.favoriteQueueUrls.splice(index, 1);
      }
    });

    if (hasDeletedQueue) {
      this.userService.setAvailablePinnedPrinterQueues(this.favoriteQueueUrls)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(() => {
        this.userService.refreshCurrentUser()
        .pipe(takeUntil(this.unSubscribe))
        .subscribe(() => {
          this.dialogService.hideLoadingSpinnerDialog('handleQueueRemoved() - hasDeletedQueue');
        });
      });
    }
  }

  // ################### /HANDLE LISTENERS #################################################################

  // ################### /LISTENERS #################################################################

  ionViewWillEnter(): void {
    this.setPageTitle();
    this.pendingDeletedJobUrls.clear();
    // Entering means foreground; guards against a resume missed during leave/enter teardown.
    this.appIsActive.set(true);
    // Re-create the teardown Subject: ionViewWillLeave completes it on every leave, and a completed Subject makes takeUntil(this.unSubscribe) a no-op.
    if (this.unSubscribe.closed || (this.unSubscribe as any).isStopped) {
      this.unSubscribe = new Subject<void>();
    }
    this.platform.ready().then(() => {
      this.logger.info('ionViewWillEnter()');
      this.getTenantAndUserSubscription.next();
      this.getTenantAndUserSubscription.complete();
      this.checkNfcAvailability();
      this.startListener();

      // Initial data load
      if (this.isOnline()) {
        this.loadPrintPage();
      }
    });
  }


  private checkNfcAvailability() {
    const methodName = 'checkNfcAvailability() ';
    this.logger.info(methodName);
    if (this.isCordova) {
      this.nfc.enabled().then((response) => {
        this.logger.info(methodName + 'constructor - NFC enabled()');
        this.nfcEnabled = true;
      }, () => {
        this.logger.info(methodName + 'constructor - NFC NOT enabled()');
      });
    }
  }

  ionViewWillLeave(): void {
    this.logger.info('ionViewWillLeave()');
    this.unSubscribe.next();
    this.unSubscribe.complete();
    // Backstop: a scan must not survive leaving the page even if some future branch misses it.
    this.concludeScan();
    this.nfcSubscription.next(); // constructor nfc initialisation subscription
    this.nfcSubscription.complete();
    if (this.nfcReaderMode) {
      this.scanning.set(false);
      this.nfcReaderMode.unsubscribe();
      if (this.isAndroid) {
        this.setPreventNFCListener();
      }
    }
    this.unsubscribePusherMessages();
    this.unsubscribeEvents();
  }

  private unsubscribePusherMessages(): void {
    this.logger.info('unsubscribePusherMessages()');
    this.jobAddedSubscription.unsubscribe();
    this.jobRemovedSubscription.unsubscribe();
    this.jobUpdatedSubscription.unsubscribe();
    this.tenantUpdatedEventSubscription.unsubscribe();
    this.updateSecurePrintSubscription.unsubscribe();
    this.releaseUpdatedSubscription.unsubscribe();
    this.createdReleaseSubscription.unsubscribe();
    this.batchExecutedSubscription.unsubscribe();
    this.printerStatusSubscription.unsubscribe();
    this.languageChangeSubscription.unsubscribe();
    this.printerRemovedSubscription.unsubscribe();
    this.printerUpdatedSubscription.unsubscribe();
    this.printQueueRemovedSubscription.unsubscribe();
    this.printQueueUpdatedSubscription.unsubscribe();
    this.printQueueGroupsUpdatedSubscription.unsubscribe();
  }

  private unsubscribeEvents() {
    this.platformPauseEventSubscription.unsubscribe();
    this.platformResumeEventSubscription.unsubscribe();
    this.platformResumeBroadcastEventSubscription.unsubscribe();
    this.reloadPageEventSubscription.unsubscribe();
  }

  public showPrinterListPage(): void {
    this.logger.info('showPrinterListPage()');
    this.navCtrl.navigateForward(['/printer-list'], {queryParams: { resource: this.releaseResource }});
  }

  public goToCapturePage() {
    this.logger.info('goToCapturePage() - actionBar (click)');
    this.navCtrl.navigateRoot(['/capture']);
  }

  public showOtherPrintJobs() { // VIEW
    this.logger.info('showOtherPrintJobs()');
    if (this.printJobsPageType === 'RecentPrintJobsPage') {
      this.navCtrl.navigateBack('/print-jobs');
    } else {
      this.navCtrl.navigateForward('/recents');
    }
  }

  ngOnDestroy(): void {
    if (!this.unSubscribe.closed && !(this.unSubscribe as any).isStopped) {
      this.unSubscribe.next();
      this.unSubscribe.complete();
    }
    this.cancelPendingReleaseUpdate.next();
    this.cancelPendingReleaseUpdate.complete();
    if (this.unSubscribeQRScanner) {
      this.unSubscribeQRScanner.next();
      this.unSubscribeQRScanner.complete();
    }
  }
}
